import * as z from 'zod'

export const ZUuid = z.uuidv4();
export type uuid = z.infer<typeof ZUuid>;

export const ZIsoDate = z.date();
export type IsoDate = z.infer<typeof ZIsoDate>;

export const ZIpAddress = z.union([z.ipv4(), z.ipv6()]);
export type IpAddress = z.infer<typeof ZIpAddress>;

export const ZPayrixDateYearMonth = z.string().regex(/\d{6}/);
export type PayrixDateYearMonth = z.infer<typeof ZPayrixDateYearMonth>;

export const ZPayrixDateYearMonthDay = z.string().regex(/\d{8}/).nullable();
export type PayrixDateYearMonthDay = z.infer<typeof ZPayrixDateYearMonthDay>;

export const ZPayrixDateYearMonthDayHourMinute = z.string().regex(/\d{12}/);
export type PayrixDateYearMonthDayHourMinute = z.infer<typeof ZPayrixDateYearMonthDayHourMinute>;

export const ZPayrixDateYearMonthDayHourMinuteSecond = z.string().regex(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)
export type PayrixDateYearMonthDayHourMinuteSecond = z.infer<typeof ZPayrixDateYearMonthDayHourMinuteSecond>;

// e.g. "2025-02-17 16:48:31.5574" or "2025-02-17 16:48:31" (millis optional — Payrix is inconsistent)
// eslint-disable-next-line security/detect-unsafe-regex -- safe-regex false positive: `\d+` is anchored behind a literal `.` inside an optional group, no catastrophic backtracking possible
export const ZPayrixDateYearMonthDayHourMinuteSecondMillis = z.string().regex(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?/)
export type PayrixDateYearMonthDayHourSecondMillis = z.infer<typeof ZPayrixDateYearMonthDayHourMinuteSecondMillis>;

export const ZISODateWithMillis = z.string().regex(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+/)
export type ISODateWithMillis = z.infer<typeof ZISODateWithMillis>;

// Reusable Zod type for MMYY format (string of 4 digits where MM is 01–12)
export const MonthYearAsMMYY = z.union([
    z.string()
        .regex(/^\d{4}$/, { message: 'Must be 4 digits in MMYY format' })
        .refine(val => {
            const month = parseInt(val.slice(0, 2), 10);
            return month >= 1 && month <= 12;
        }, { message: 'Month must be between 01 and 12' }),
    z.null()
]).optional();

/**
 * Payrix Boolean, 0 or 1: 1 is true for whatever the field is.
 * For example, if the field is cvv, then 1 means cvv was entered.
 */
export enum EPayrixBool {
    False = 0,
    True = 1
}

export const ZPayrixBool = z.number().gte(0).lte(1);
export type PayrixBool = z.infer<typeof ZPayrixBool>;
/**
 * Payrix uses the entity field to represent a unique ID in the system.
 */
export const ZPayrixEntity = z.string().length(30);
export type PayrixEntity = z.infer<typeof ZPayrixEntity>;
export const ZPayrixEntityWithSuffix = z.string().min(30).max(34);

/**
 * @enum {number}
 * @readonly
 */
export enum EAccountInactive {
    Active = 0,
    Inactive = 1
}

export const ZAccountInactive = z.enum(EAccountInactive);
export type AccountInactive = z.infer<typeof ZAccountInactive>;

/**
 * @enum {number}
 * @readonly
 */
export enum EAccountFrozen {
    NotFrozen = 0,
    Frozen = 1
}

export const ZAccountFrozen = z.enum(EAccountFrozen);
export type AccountFrozen = z.infer<typeof ZAccountFrozen>;

/**
 * Base class for all Payrix objects
 */
export const ZPayrixEntityHeader = z.object({
    id: ZPayrixEntity.optional().nullable(), // unique id for this Merchant
    login: ZPayrixEntity.optional().nullable(), // The login that created this entity
    created: ZPayrixDateYearMonthDayHourMinuteSecondMillis.optional().nullable(), // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: ZPayrixDateYearMonthDayHourMinuteSecondMillis.optional().nullable(), // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: ZPayrixEntity.optional().nullable(), // id of the Login that created the entity (don't need when creating)
    modifier: z.string().optional().nullable(), // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    inactive: ZPayrixBool.optional().default(EAccountInactive.Active), // Whether this resource is marked as inactive.
    frozen: ZPayrixBool.optional().default(EAccountFrozen.NotFrozen), // Whether this resource is marked as frozen.
});

/**
 * Base class for all Payrix objects
 */
export const ZPayrixEntityHeaderResult = z.object({
    id: ZPayrixEntity, // unique id for this Merchant
    created: ZPayrixDateYearMonthDayHourMinuteSecondMillis, // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: ZPayrixDateYearMonthDayHourMinuteSecondMillis, // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: ZPayrixEntity, // id of the Login that created the entity (don't need when creating)
    modifier: z.string().optional().nullable(), // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    inactive: ZPayrixBool, // Whether this resource is marked as inactive.
    frozen: ZPayrixBool, // Whether this resource is marked as frozen.
});

export const ZPayrixEntityHeaderLoginRequired = ZPayrixEntityHeader.extend({
    login: ZPayrixEntity, // The login that created this entity
});

// Detail data object containing specific error code information.
export type PayrixResponseDetails = {
    requestId: number,      // The identifier for the request sent.
    totals: number,         // The total amount used in the transaction.
    page: {                 // Page data object containing page number and availability of more records on other pages.
        current: number,    // The current page of records returned.
        last: number,       // The last page containing records to be returned.
        hasMore: boolean,   // Whether there are more records that can be fetched.
    }
}

export enum EPayrixSeverity {
    Info = 1,           // Info notice, but processing may continue
    Error = 2,          // Error warning. Processing should stop.
    Failure = 3,        // Failure error. Failure notice. Processing should stop.
    TrackingError = 4,  // Temporary tracking notice. Processing should stop
}

export const ZPayrixSeverity = z.enum(EPayrixSeverity);
export type PayrixSeverity = z.infer<typeof ZPayrixSeverity>;

// A single Payrix error, part of a larger response
export type PayrixError = {
    field: string;              // This is the field that had an error.
    code: number;               // Returns the error code within the Payrix system.
    severity: EPayrixSeverity;  // Returns the gravity of the error.
    msg: string;                // A quick description or message about the error.
    errorCode: string;          // Indicating the error code and severity, explained in the msg field.
};

// Payrix errors as returned in a response
export type PayrixErrors = PayrixError[];

export type PayrixResponse<T> = {
    data: T[],
    details: PayrixResponseDetails,
    errors: PayrixErrors
}
// A full response sent back from the server
export type PayrixQuery<T> = Partial<{
    response: PayrixResponse<T>,
    errors: PayrixErrors,
}>;

/* Payment Methods */
export enum EPaymentMethods {
    AmericanExpress = 1,
    Visa = 2,
    Mastercard = 3,
    DinersClub = 5, // Bought by Discover, uses same code now
    Discover = 5,
    CheckingAccount = 8,
    SavingsAccount = 9,
    CorporateCheckingAccount = 10,
    CorporateSavingsAccount = 11,
}

export const ZPaymentMethods = z.enum(EPaymentMethods);
export type PaymentMethods = z.infer<typeof ZPaymentMethods>;

/* Payment Types */
export enum EPaymentType {
    Bank = 'bank',
    Card = 'card'
}

export const ZPaymentType = z.enum(EPaymentType);
export type PaymentType = z.infer<typeof ZPaymentType>;

export enum ETransactionPlatform {
    Apple = 'APPLE',
    Elavon = 'ELAVON',
    FirstData = 'FIRSTDATA',
    Google = 'GOOGLE',
    Vantiv = 'VANTIV',
    Vcore = 'VCORE',
    WellsAch = 'WELLSACH',
    WellsFargo = 'WELLSFARGO',
    WFSingle = 'WFSINGLE',
    Worldpay = 'WORLDPAY',
    TDBankCanada = "TDBANKCA",
}

export const ZTransactionPlatform = z.enum(ETransactionPlatform);
export type TransactionPlatform = z.infer<typeof ZTransactionPlatform>;

export enum ETransactionStatus {
    Pending = 0,
    Approved = 1,
    Failed = 2,
    Captured = 3,
    Settled = 4,
    Returned = 5
}

export const ZTransactionStatus = z.enum(ETransactionStatus);
export type TransactionStatus = z.infer<typeof ZTransactionStatus>;

export enum ETransactionType {
    CreditCardSale = 1, // Processes a sale and charges the customer
    CreditCardAuth = 2, // Authorizes and holds the requested total on the credit card
    CreditCardCapture = 3, // Finalizes a prior Auth Transaction and charges the customer
    CreditCardReverseAuth = 4, // Reverses a prior Auth or Sale Transaction and releases the credit hold
    CreditCardRefund = 5, // Refunds a prior Capture or Sale Transaction (total may be specified for a partial refund)
    ECheckSale = 7, // Sale Transaction for ECheck payment
    ECheckRefund = 8, // Refund Transaction for prior ECheck Sale Transaction
    ECheckRedeposit = 11, // Attempt to redeposit a prior failed eCheck Sale Transaction
    ECheckAccountVerification = 12, // Attempt to verify eCheck payment details.
}

const ZTransactionType = z.enum(ETransactionType);
export type TransactionType = z.infer<typeof ZTransactionType>;

export enum ETransactionTypeName {
    CreditCardSale = 'Credit Card Sale',
    CreditCardAuth = 'Credit Card Auth',
    CreditCardCapture = 'Credit Card Capture',
    CreditCardReverseAuth = 'Credit Card Reverse Auth',
    CreditCardRefund = 'Credit Card Refund',
    ECheckSale = 'E-Check Sale',
    ECheckRefund = 'E-Check Refund',
    ECheckRedeposit = 'E-Check Redeposit',
    ECheckAccountVerification = 'E-Check Account Verification'
}

export const ZTransactionTypeName = z.enum(ETransactionTypeName);
export type TransactionTypeName = z.infer<typeof ZTransactionTypeName>;

export enum ETransactionStatusDescription {
    CreditCardSale = "Processes a sale and charges the customer",
    CreditCardAuth = "Authorizes and holds the requested total on the credit card",
    CreditCardCapture = "Finalizes a prior Auth Transaction and charges the customer",
    CreditCardReverseAuth = "Reverses a prior Auth or Sale Transaction and releases the credit hold",
    CreditCardRefund = "Refunds a prior Capture or Sale Transaction (total may be specified for a partial refund)",
    ECheckSale = "Sale Transaction for ECheck payment",
    ECheckRefund = "Refund Transaction for prior ECheck Sale Transaction",
    ECheckRedeposit = "Attempt to redeposit a prior failed eCheck Sale Transaction",
    ECheckAccountVerification = "Attempt to verify eCheck payment details",
}

export const ZTransactionStatusDescription = z.enum(ETransactionStatusDescription);
export type TransactionStatusDescription = z.infer<typeof ZTransactionStatusDescription>;

// export enum ETransactionStatusName {
//     Authorized = 'AUTHORIZED',
//     Completed = 'COMPLETED',
//     Pending = 'PENDING',
//     Voided = 'VOIDED',
//     Failed = 'FAILED'
// }
//
// export const ZTransactionStatusName = z.enum(ETransactionStatusName);
// export type TransactionStatusName = z.infer<typeof ZTransactionStatusName>;

export enum ETransactionStatusName {
    Pending = "Pending",
    Approved = "Approved",
    Failed = "Failed",
    Captured = "Captured",
    Settled = "Settled",
    Returned = "Returned",
    Voided = "Voided"
}

export const ZTransactionStatusName = z.enum(ETransactionStatusName);
export type TransactionStatusName = z.infer<typeof ZTransactionStatusName>;

export enum EStatusDescriptions {
    Pending = "The Gateway is awaiting confirmation from the processor of the transaction's creation.",
    Approved = "The transaction has been approved (authorized).", // Same as Authorized in LawPay
    Failed = "The transaction failed. Consult the failure code for details.",
    Captured = "The transaction has been captured. Funds have not been deposited and are not ready to be distributed",
    Settled = "The Gateway has completed processing the transaction and the funds are ready to be distributed.",
    Returned = "The transaction has been returned."
}

export enum EChargebackStatus {
    Open = 'Open',
    Closed = 'Closed',
    Won = 'Won',
    Lost = 'Lost'
}

export const ZChargebackStatus = z.enum(EChargebackStatus);
export type ChargebackStatus = z.infer<typeof ZChargebackStatus>;

export enum EChargebackMessageStatus {
    Requested = 'requested',
    Processing = 'processing',
    Failed = 'failed',
    Denied = 'denied',
    Processed = 'processed'
}

export const ZChargebackMessageStatus = z.enum(EChargebackMessageStatus);
export type ChargebackMessageStatus = z.infer<typeof ZChargebackMessageStatus>;

export enum EChargebackMessageResultType {
    General = 'general',
    Platform = 'platform',
}

export const ZChargebackMessageResultType = z.enum(EChargebackMessageResultType);
export type ChargebackMessageResultType = z.infer<typeof ZChargebackMessageResultType>;

export enum ECardOnFileType {
    Single = 'single',
    Scheduled = 'scheduled',
    Unscheduled = 'unscheduled',
}

export const ZCardOnFileType = z.enum(ECardOnFileType);
export type CardOnFileType = z.infer<typeof ZCardOnFileType>;

export enum EChargebackMessageType {
    Assign = 'assign',
    Notate = 'notate',
    AcceptLiability = 'acceptLiability',
    CreatePreArbitration = 'createPreArbitration',
    Represent = 'represent',
    Respond = 'respond',
    RequestArbitration = 'requestArbitration',
    CreateArbitration = 'createArbitration',
    RequestPreArbitration = 'requestPreArbitration',
    RequestResolutionToPreArbitration = 'requestResolutionToPreArbitration',
    RespondToDispute = 'respondToDispute',
    RespondToPreArbitration = 'respondToPreArbitration',
    Unaccept = 'unaccept',
    FilePreArbitration = 'filePreArbitration'
}

export const ZChargebackMessageType = z.enum(EChargebackMessageType);
export type ChargebackMessageType = z.infer<typeof ZChargebackMessageType>;

/**
 * Merchant Types for onboarding
 */
export enum EMerchantType {
    SoleProprietor = 0,
    Corporation = 1,
    LimitedLiabilityCorporation = 2,
    Partnership = 3,
    NonProfitOrganization = 5,
    GovernmentOrganization = 6,
    CCorporation = 7,
    SCorporation = 8
}

/**
 * The entity types Outlaw's application form offers.
 *
 * OWA-711: `EMerchantType` mirrors the raw API enum, but three of its values
 * are absent from every Payrix onboarding surface — the signup-form
 * definitions, the Business Information guide, and the onboarding risk
 * best-practices page all list only 0, 1, 2, 3, 5, 6. Since `type` is
 * immutable at Payrix once the TIN validates, offering an undocumented value
 * risks an uncorrectable wrong answer:
 *
 *   - `Association` (4) — a Florida P.A. is a professional service corporation
 *     under Fla. Stat. Ch. 621, so `Corporation` is both documented and
 *     legally accurate.
 *   - `CCorporation` (7) / `SCorporation` (8) — Subchapter S and C are IRS tax
 *     ELECTIONS, not legal forms. The secretary of state registered a
 *     corporation; the tax identity travels via the TIN and IRS filing name.
 *
 *   - `GovernmentOrganization` (6) is documented but excluded from the form:
 *     government entities must carry 0% ownership, while every other entity
 *     must list at least 1% or fail auto-boarding. The form cannot satisfy
 *     both, and a government law office is not an Outlaw customer.
 */
export const OFFERED_MERCHANT_TYPES = [
    EMerchantType.SoleProprietor,
    EMerchantType.Corporation,
    EMerchantType.LimitedLiabilityCorporation,
    EMerchantType.Partnership,
    EMerchantType.NonProfitOrganization,
] as const;

const ZMerchantType = z.enum(EMerchantType);
export type MerchantType = z.infer<typeof ZMerchantType>;

/**
 * @enum {number}
 * @readonly
 */
export enum EDisbursementStatus {
    Requested = 1,
    Processing = 2,
    Processed = 3,
    Failed = 4,
    Denied = 5,
    Returned = 6
}

export const ZDisbursementStatus = z.enum(EDisbursementStatus);
export type DisbursementStatus = z.infer<typeof ZDisbursementStatus>;

export enum EPayrixNoteDocumentStatus {
    Created = 'created',
    Processed = 'processed',
    Failed = 'failed',
}

export const ZPayrixNoteDocumentStatus = z.enum(EPayrixNoteDocumentStatus);
export type PayrixNoteDocumentStatus = z.infer<typeof ZPayrixNoteDocumentStatus>;

export enum EPayrixNoteDocumentType {
    General = 'general',
    PersonalId = 'personalId',
    CompanyId = 'companyId',
    VoidCheck = 'voidCheck',
    BankStatement = 'bankStatement',
    BankLetter = 'bankLetter',
    Contract = 'contract',
    TaxDocument = 'taxDocument',
}

export const ZPayrixNoteDocumentType = z.enum(EPayrixNoteDocumentType);
export type PayrixNoteDocumentType = z.infer<typeof ZPayrixNoteDocumentType>;

export enum EPayrixDocumentType {
    JPG = 'JPG',
    JPEG = 'JPEG',
    GIF = 'GIF',
    PNG = 'PNG',
    PDF = 'PDF',
    TIFF = 'TIFF',
    TIF = 'TIF',
}

export const ZPayrixDocumentType = z.enum(EPayrixDocumentType);
export type PayrixDocumentType = z.infer<typeof ZPayrixDocumentType>;

export enum EChargebackDocumentStatus {
    Created = 'Created. The ChargebackDocument has been created.',
    Processed = 'Processed. The ChargebackDocument integration has been successful.',
    Failed = 'Failed. The ChargebackDocument integration has failed.',
}

export const ZChargebackDocumentStatus = z.enum(EChargebackDocumentStatus);
export type ChargebackDocumentStatus = z.infer<typeof ZChargebackDocumentStatus>;

export enum EChargebackDocumentSource {
    Merchant = 'Merchant',
    Issuer = 'Issuer',
}

export const ZChargebackDocumentSource = z.enum(EChargebackDocumentSource);
export type ChargebackDocumentSource = z.infer<typeof ZChargebackDocumentSource>;

/**
 * Account types - IOLTA is 'credit' , operating is 'all'
 * @enum {string}
 * @readyonly
 */
export enum EAccountType {
    Credit = 'credit',
    Debit = 'debit',
    All = 'all'
}

export const ZAccountType = z.enum(EAccountType);
export type AccountType = z.infer<typeof ZAccountType>;

/**
 * Primary values for an Account
 *
 * @enum {number}
 * @readonly
 */
export enum EAccountPrimary {
    NotPrimary = 0,
    Primary = 1
}

export const ZAccountPrimary = z.enum(EAccountPrimary);
export type AccountPrimary = z.infer<typeof ZAccountPrimary>;

/**
 * Account Methods
 *
 * @enum {number}
 * @readonly
 */
export enum EAccountMethod {
    Checking = 8,
    Savings = 9,
    CorporateChecking = 10,
    CorporateSavings = 11
}

export const ZAccountMethod = z.enum(EAccountMethod);
export type AccountMethod = z.infer<typeof ZAccountMethod>;

/**
 * @enum {string}
 * @readonly
 */
export enum EChargebackCycle {
    Retrieval = 'Retrieval', // Initial request from the issuer for more information on this Transaction
    First = 'First', // First Chargeback from issuer for this Transaction.
    Arbitration = 'Arbitration', // Arbitration is being sought for this Chargeback.
    Reversal = 'Reversal', // Chargeback was reversed.
    Representment = 'Representment', // Merchant is being represented to the issuer with the Chargeback response posted.
    PreArbitration = 'PreArbitration', // Chargeback is no longer representable. Merchant must choose to accept or arbitrate with the card brand.
    ArbitrationLost = 'ArbitrationLost',
    ArbitrationSplit = 'ArbitrationSplit',
    ArbitrationWon = 'ArbitrationWon',
    IssuerAcceptPreArbitration = 'IssuerAcceptPreArbitration', // Issuer accepted the pre-arbitration response.
    IssuerDeclinedPreArbitration = 'IssuerDeclinedPreArbitration', // Issuer declined pre-arbitration.
    ResponseToIssuerPreArbitration = 'ResponseToIssuerPreArbitration', // Response to issuer pre-arbitration
    MerchantAcceptedPreArbitration = 'MerchantAcceptedPreArbitration',
    MerchantDeclinedPreArbitration = 'MerchantDeclinedPreArbitration',
    PreCompliance = 'PreCompliance',
    Compliance = 'Compliance'
}

export const ZChargebackCycle = z.enum(EChargebackCycle);
export type ChargebackCycle = z.infer<typeof ZChargebackCycle>;

/**
 * Account Status
 */
export enum EAccountStatus {
    NotReady = 0, // The account holder is not yet ready to verify the Account
    Ready = 1, // The account is ready to be verified
    Challenged = 2, // The account has processed the challenge
    Verified = 3, // The account has been verified
    Manual = 4, // There has been an inssue during verification and further attempts to verify the Account will require manual intervention
}

export const ZAccountStatus = z.enum(EAccountStatus);
export type AccountStatus = z.infer<typeof ZAccountStatus>;

/**
 * OWA-679: the firm-facing state of a Payrix merchant application. Derived
 * server-side (see server/src/payments/payrix/applicationStatus.ts) from a live
 * read of the merchant + entity, because Payrix warns that webhook events
 * arrive out of order and duplicate.
 */
export enum EApplicationState {
    None = 'none',
    NotReady = 'notReady',
    UnderReview = 'underReview',
    ManualReview = 'manualReview',
    Incomplete = 'incomplete',
    Declined = 'declined',
    Approved = 'approved',
    ConditionallyApproved = 'conditionallyApproved',
    PayoutsPaused = 'payoutsPaused',
    Disabled = 'disabled',
    Failed = 'failed',
}

export const ZApplicationState = z.enum(EApplicationState);
export type PayrixApplicationState = z.infer<typeof ZApplicationState>;

export const ZPayrixApplicationStatusInfo = z.object({
    state: ZApplicationState,
    merchantStatus: z.number().nullable(),
    statusReason: z.string().nullable(),
    canProcessPayments: z.boolean(),
    payoutsPaused: z.boolean(),
    actionRequired: z.boolean(),
});
export type PayrixApplicationStatusInfo = z.infer<typeof ZPayrixApplicationStatusInfo>;

/**
 * OWA-745: the last status the server stored, served when Payrix cannot be
 * reached. It carries no eligibility flags: a stale read grants nothing.
 */
export const ZPayrixStoredApplicationStatus = z.object({
    state: ZApplicationState,
    merchantStatus: z.number().nullable(),
    statusReason: z.string().nullable(),
    /** When the status was observed, ISO 8601. */
    observedAt: z.string(),
    stale: z.literal(true),
});
export type PayrixStoredApplicationStatus = z.infer<typeof ZPayrixStoredApplicationStatus>;

export const ZPayrixApplicationStatus = z.object({
    firmId: z.uuidv4(),
    application: z.string(),
    applicationStatus: z.number(),
    login: z.string(),
    operatingUploadCompleted: z.boolean(),
    trustUploadCompleted: z.boolean(),
});
export type PayrixApplicationStatus = z.infer<typeof ZPayrixApplicationStatus>;

export type PayrixMerchantAccount = {
    method: EAccountMethod; //
    number: string; // account number
    routing: string; // routing number
};
//
// enum EPayrixPlatform {
//     Apple = 'APPLE',
//     Elavon = 'ELAVON',
//     FirstData = 'FIRSTDATA',
//     Google = 'GOOGLE',
//     Vantiv = 'VANTIV',
//     Vcore = 'VCORE',
//     WellsAch = 'WELLSACH',
//     WellsFargo = 'WELLSFARGO',
//     WellsFargeSingle = 'WFSINGLE',
//     WorldPay = 'WORLDPAY',
//     TDBankCA = 'TDBANKCA',
// }
// const ZPayrixPlatform = z.enum(EPayrixPlatform);
// export type PayrixPlatform = z.infer<typeof ZPayrixPlatform>;

/**
 * OWA-677: the wire contract for the merchant application as the CLIENT SENDS
 * IT (pre-normalization: money in dollars, phone/EIN/SSN may carry punctuation
 * — the server strips and converts before forwarding to Payrix). The previous
 * schemas here had drifted into unusable dead code: the member schema was a
 * copy of the account schema, bank details were at the wrong nesting level,
 * and fields the client never sent were required. Rewritten from the client
 * model (CPayrixApplication) and the Payrix API specification, and now
 * actually enforced by POST /payrix/application.
 */

/** Payrix timezone enum (SPEC `entitiesTimezone` / `MembersTimezone`). */
export const ZPayrixTimezone = z.enum(['est', 'cst', 'pst', 'mst', 'akst', 'hst', 'sst', 'chst', 'ast', 'pwt', 'mht', 'chut', 'nst']);
export type PayrixTimezoneEnum = z.infer<typeof ZPayrixTimezone>;

/**
 * A money amount as entered in the UI: DOLLARS, string or number. The server
 * converts to integer cents (dollarsToCents throws on NaN, so reject early).
 */
const ZDollarAmount = z.union([z.string(), z.number()])
    .refine(v => v !== '' && Number.isFinite(Number(v)), 'must be a number (in dollars)');

/**
 * OWA-711: exactly 10 digits, no country code. Payrix's onboarding guide is
 * explicit that a leading `1` causes boarding failures ("Do: 800-111-1111,
 * Don't: 1-800-111-1111"), and the server strips punctuation before sending —
 * so an 11-digit value arrives at Payrix intact. Punctuation is still allowed
 * here because that is what people type.
 */
const ZUsPhone = z.string()
    .refine(v => v.replace(/\D/g, '').length === 10,
        'phone must be 10 digits with no country code');

/**
 * OWA-711: Payrix rejects P.O. Box addresses for every entity type, on both the
 * business address and every owner address. Mirrors `isPoBox` on the client.
 */
const ZStreetAddress = z.string().min(1)
    .refine(v => !/\b(?:p\.?\s*o\.?\s*box|post\s+office\s+box|p\.?\s*o\.?\s*b)\b/i.test(v.trim()),
        'P.O. Box addresses are not accepted');

/**
 * OWA-711: an ampersand in a principal's title "can result in boarding
 * failure" per the onboarding risk best-practices page.
 */
const ZBusinessTitle = z.string().min(1).max(100)
    .refine(v => !v.includes('&'), 'title cannot contain an ampersand (&)');

/**
 * OWA-711: 5 or 9 digits for US ("String of numbers that have either 5 or 9
 * characters"), or a Canadian postal code. ZIP+4 may carry a hyphen.
 */
const ZPostalCode = z.string()
    .refine(v => {
        const t = v.trim().toUpperCase();
        if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(t)) return true;   // CAN
        const digits = t.replace(/\D/g, '');
        return digits.length === 5 || digits.length === 9;
    }, 'postal code must be 5 or 9 digits (US) or a Canadian postal code');

/** Manually-entered bank details, nested under `account` as the client sends them. */
const ZPayrixApplicationBankInfo = z.object({
    method: ZPaymentMethods.default(10), // 8/9 personal, 10/11 business checking/savings
    number: z.string().min(4).max(20),   // spec: up to 20 characters
    routing: z.string().min(5).max(20),
});

const ZPayrixApplicationAccount = z.object({
    primary: ZPayrixBool,
    currency: z.literal('USD'),          // spec-REQUIRED; US platform only
    type: ZAccountType,
    name: z.string().min(1),             // display label, e.g. 'Operating' / 'IOLTA/Trust'
    description: z.string().optional(),
    account: ZPayrixApplicationBankInfo.optional(), // manual entry
    platform: z.string().optional(),     // 'PLAID' when Plaid-linked
    publicToken: z.string().optional(),
    accountToken: z.string().optional(),
    file: z.any().optional(),            // voided check (pruned before Payrix)
    fileContent: z.string().optional(),
    metadata: z.any().optional(),
}).refine(
    a => a.platform === 'PLAID' ? (!!a.publicToken && !!a.accountToken) : !!a.account,
    'account needs bank details or a completed Plaid link',
);

export type PayrixApplicationAccount = z.infer<typeof ZPayrixApplicationAccount>;

export enum EAccountMode {
    Operating = 'operating',
    Trust = 'IOLTA/Trust'
}

export const ZAccountMode = z.enum(EAccountMode);
export type AccountMode = z.infer<typeof ZAccountMode>;

/** A beneficial owner / control person, as the client sends it (KYC). */
const ZPayrixApplicationMember = z.object({
    primary: ZPayrixBool.optional(),
    title: ZBusinessTitle,
    first: z.string().min(1),
    middle: z.string().optional(),
    last: z.string().min(1),
    dob: z.string().regex(/^\d{8}$/, 'dob must be YYYYMMDD'),
    /**
     * INTEGER basis points, 100-10000 (25.3% = 2530).
     *
     * OWA-711: the floor is 100 (= 1%), not 1. The onboarding risk
     * best-practices page is explicit: every entity except a government one
     * "must list at least 1%", and "if at least 1% is not included, the
     * account will FAIL auto-boarding". `min(1)` accepted 0.01%, which passes
     * here and then fails at Payrix days later.
     */
    ownership: z.number().int().min(100).max(10000),
    significantResponsibility: ZPayrixBool,
    politicallyExposed: ZPayrixBool,
    email: z.email('Not a valid email address'),
    phone: ZUsPhone,                     // punctuation allowed; server strips
    fax: z.string().optional(),
    ssn: z.string().min(9),              // punctuation allowed; server strips
    address1: ZStreetAddress,
    address2: z.string().optional(),
    city: z.string().min(1),
    state: z.string().min(2),
    zip: ZPostalCode,
    country: z.enum(['USA', 'CAN']),
    timezone: ZPayrixTimezone,           // spec-REQUIRED per member
});
export type PayrixApplicationMember = z.infer<typeof ZPayrixApplicationMember>;

export const ZPayrixApplicationMerchant = z.object({
    id: ZPayrixEntity.optional(),        // present on the Payrix response only
    dba: z.string().min(1).max(50),      // OWA-711: "String between 0 and 50 characters"
    /** 1 = never accepted cards. Prior-year sales only expected when 0. */
    new: ZPayrixBool,
    established: z.string().regex(/^\d{8}$/, 'established must be YYYYMMDD'),
    annualCCSales: ZDollarAmount.optional(),
    avgTicket: ZDollarAmount,
    totalVolume: ZDollarAmount,
    percentEcomm: z.number().int().min(0).max(100),
    percentKeyed: z.number().int().min(0).max(100),
    /** Must be accepted (1) to submit — the authorized-signer attestation. */
    tcAttestation: z.literal(1),
    /** Must be accepted (1) to submit — the card-brand/bank disclosure. */
    visaDisclosure: z.literal(1),
    disclosureDate: z.number(),
    disclosureIP: ZIpAddress.optional(), // server fills from the request
    mcc: z.literal('8111'),
    status: z.number().optional(),
    environment: z.enum(['moto', 'ecommerce']),
    seasonal: ZPayrixBool.optional(),
    advancedBilling: ZPayrixBool.optional(),
    saqDate: z.string().optional(),
    members: z.array(ZPayrixApplicationMember).min(1),
}).refine(
    m => m.new === 1 || m.annualCCSales !== undefined,
    {path: ['annualCCSales'], error: 'annualCCSales is required for firms that have processed cards before'},
).refine(
    m => m.members.reduce((acc, x) => acc + x.ownership, 0) <= 10000,
    {path: ['members'], error: 'combined ownership across all members cannot exceed 100%'},
).refine(
    // OWA-711: "Only one Member associated with each merchant can be the
    // 'primary' Member." This used to be `.some(...)`, which let two primaries
    // through while the message claimed otherwise — the UI enforces radio
    // behaviour, but this schema is the untrusted-input boundary.
    m => m.members.filter(x => x.primary === 1).length === 1,
    {path: ['members'], error: 'exactly one member must be the primary owner'},
);
export type PayrixApplicationMerchant = z.infer<typeof ZPayrixApplicationMerchant>;

export enum EPayrixCurrency {
    USD = 'USD',
    CAN = 'CAN'
}

export const ZPayrixCurrency = z.enum(EPayrixCurrency);
export type PayrixCurrency = z.infer<typeof ZPayrixCurrency>;

export const ZPayrixOrgEntry = z.object({
    org: ZPayrixEntity
});

export type PayrixOrgEntry = z.infer<typeof ZPayrixOrgEntry>;

export const ZPayrixApplication = z.object({
    id: ZPayrixEntity.optional(),        // present on the Payrix response only
    type: ZMerchantType,                 // required — no default; immutable at Payrix after TIN validation
    name: z.string().min(1),
    address1: ZStreetAddress,
    address2: z.string().optional(),
    city: z.string().min(1),
    state: z.string().length(2),
    zip: ZPostalCode,
    country: z.enum(['USA', 'CAN']),
    phone: ZUsPhone,                     // punctuation allowed; server strips
    fax: z.string().optional(),
    email: z.email('Not a valid email address'),
    einType: z.enum(['tin', 'ssn']),
    ein: z.string().min(9),              // punctuation allowed; server strips
    website: z.url('Not a valid website url'),
    timezone: ZPayrixTimezone,           // spec-REQUIRED on the entity
    custom: z.string().optional(),       // server-owned; overwritten from the JWT (OWA-649)
    // OWA-711: free text, 0-1000 chars, optional. Hardcoded to 'Legal Services'
    // by the client — every Outlaw customer is a law firm.
    industry: z.string().max(1000).optional(),
    // OWA-711: required when `name` exceeds 24 characters, capped at 40.
    // Enforced conditionally by the refine below.
    irsFilingName: z.string().max(40).optional(),
    tcVersion: z.string().min(1),
    tcDate: z.string().min(1),           // yyyy-MM-dd HH:mm:ss — confirmed against prod
    // OWA-711: null on every production entity, so Payrix does not rely on it.
    // Kept (and format-checked when present) rather than dropped, because the
    // onboarding checklist still lists it.
    tcAcceptDate: z.string().regex(/^\d{12}$/, 'tcAcceptDate must be YYYYMMDDHHII').optional(),
    tcAcceptIp: ZIpAddress.optional(),   // server fills from the request
    clientIp: ZIpAddress.optional(),     // server fills from the request
    payoutSecondaryDescriptor: z.string().optional(),
    // OWA-711: privately vs publicly HELD. Only meaningful for a Corporation (1)
    // or an LLC (2) — "If the ENTITY TYPE is not 1 or 2, leave blank" — so the
    // client omits it for every other type rather than asserting 0.
    public: ZPayrixBool.optional(),
    locations: z.number().optional(),
    reserved: z.number().optional(),
    inactive: z.number().optional(),
    frozen: z.number().optional(),
    accounts: z.array(ZPayrixApplicationAccount).length(2), // Operating + IOLTA/Trust
    orgEntities: z.array(ZPayrixOrgEntry).optional(), // server-owned; overwritten with our fee/withdrawal orgs
    merchant: ZPayrixApplicationMerchant,
}).refine(
    // OWA-711: "IRS Filing Name is required if the Business Legal Name provided
    // is greater than 24 characters. Must match what was provided to IRS."
    a => a.name.length <= 24 || !!a.irsFilingName,
    {path: ['irsFilingName'], error: 'irsFilingName is required when the legal name exceeds 24 characters'},
).refine(
    // OWA-711: `public` is a share-ownership question. Asserting it for a sole
    // proprietor, partnership, non-profit or government body is meaningless.
    a => (a.type === 1 || a.type === 2) || a.public === undefined,
    {path: ['public'], error: 'public only applies to a Corporation or an LLC'},
).refine(
    // OWA-711: the form offers a deliberate subset of the API's entity types —
    // see OFFERED_MERCHANT_TYPES for why each exclusion. Enforced here too so a
    // hand-crafted body cannot board as an S Corp or a Government entity, both
    // of which we have decided we cannot support correctly.
    a => (OFFERED_MERCHANT_TYPES as readonly number[]).includes(a.type),
    {path: ['type'], error: 'unsupported business type'},
);
export type PayrixApplication = z.infer<typeof ZPayrixApplication>;


export const ZCardPaymentMethods = z.number().min(EPaymentMethods.Visa).max(EPaymentMethods.DinersClub);
export type CardPaymentMethods = EPaymentMethods.Visa | EPaymentMethods.Mastercard | EPaymentMethods.AmericanExpress | EPaymentMethods.Discover | EPaymentMethods.DinersClub;

export const ZBankPaymentMethods = z.number().min(EPaymentMethods.CheckingAccount).max(EPaymentMethods.CorporateSavingsAccount);
export type BankPaymentMethods = EPaymentMethods.CheckingAccount | EPaymentMethods.SavingsAccount | EPaymentMethods.CorporateCheckingAccount | EPaymentMethods.CorporateSavingsAccount;

export enum ECardPaymentMethodNames {
    AmericanExpress = 'AMEX',
    Visa = 'Visa',
    Mastercard = 'Mastercard',
    DinersClub = 'Diners Club',
    Discover = 'Discover'
}

export const ZCardPaymentMethodNames = z.enum(ECardPaymentMethodNames);
export type CardPaymentMethodNames = z.infer<typeof ZCardPaymentMethodNames>;

export enum EBankPaymentMethodNames {
    IndividualChecking = 'Individual Checking',
    IndividualSavings = 'Individual Savings',
    BusinessChecking = 'Business Checking',
    BusinessSavings = 'Business Savings'
}

export const ZBankPaymentMethodNames = z.enum(EBankPaymentMethodNames);
export type BankPaymentMethodNames = z.infer<typeof ZBankPaymentMethodNames>;

export const ZPaymentMethodNames = z.union([ZCardPaymentMethodNames, ZBankPaymentMethodNames]);
export type PaymentMethodNames = z.infer<typeof ZPaymentMethodNames>;

export enum EReserved {
    NoReserve = 0,
    AccountWithheld = 1,
    AccountUsagePendingManualReview = 2,
    MoveAllFundsIntoReserve = 3
}

export const ZReserved = z.enum(EReserved);
export type Reserved = z.infer<typeof ZReserved>;

/**
 * The current stage from risk. Could be for creating the merchant itself, or for creating a bank account (createAccount)
 */
export enum ECheckStage {
    createEntity = 'createEntity', // Merchant is being created
    underwriting = 'underwriting', // Merchant is in underwriting
    preboard = 'preboard', // Check the Merchant before they are boarded
    postboard = 'postboard', // Check the Merchant after they are boarded
    txn = 'txn', // Check the Merchant when they process a Transaction
    txnVolume = 'txnVolume', // Check the Merchant when their transaction volume hits a certain amount
    payout = 'payout', // Check the Merchant when a Payouts occurs
    payoutVolume = 'payoutVolume', // Check the Merchant when the volume of Payouts to the Merchant his a certain amount
    createAccount = 'createAccount', // Bank account has been created (used only when creating bank accounts)
}

export const ZCheckStage = z.enum(ECheckStage);
export type CheckStage = z.infer<typeof ZCheckStage>;

export const ZPayrixAccountInfo = z.object({
    method: ZBankPaymentMethods.optional(),
    number: z.string().optional(),
    routing: z.string().optional()
});
export type PayrixAccountInfo = z.infer<typeof ZPayrixAccountInfo>;

export enum EUpdateMethod {
    NotificationOfChange = 'notificationOfChange',
    Plaid = 'Updated by Plaid',
    Manual = 'Updated manually'
}

export const ZUpdateMethod = z.enum(EUpdateMethod);
export type UpdateMethod = z.infer<typeof ZUpdateMethod>;

// Account structure
export const ZPayrixAccount = ZPayrixEntityHeader.extend({
    entity: ZPayrixEntity, // the entityId of the Merchant that owns this account
    token: z.string().optional(), // return only, token used throughout this api
    name: z.string(), // optional name for the account, e.g. IOLTA or Operating
    account: ZPayrixAccountInfo.optional(), // Part of the response, *never an input parameter*
    primary: ZAccountPrimary, // primary status of the account
    type: ZAccountType, // credit, debit or all
    status: ZAccountStatus, // status of the account
    currency: ZPayrixCurrency, // defaults to USD
    checkStage: ZCheckStage.nullable(), // optional, only valid value is 'createAccount' according to docs
    expiration: MonthYearAsMMYY, // The expiration date of the related debit account. The date is specified as a four digit string in MMYY format, for example, '0118' for January, 2018
    description: z.string().optional().nullable(), // optional description for the account
    publicToken: z.string().optional(), // The token received from the plaid account connection process
    plaidAccountId: z.string().optional().nullable(), // return only, the Plaid account id
    updateMethod: ZUpdateMethod.optional().nullable(), // No idea what this is for
    reserved: ZReserved, // Indicates whether the account is reserved and the action that will be taken as a result
    mask: z.string().optional().nullable(), // The last 4 of the account number, if available
});
export type PayrixAccount = z.infer<typeof ZPayrixAccount>;

/** Outlaw Practice uses two types of accounts, IOLTA (trust) and Operating */
export const ZBankAccounts = z.object({
    iolta: ZPayrixAccount,
    operating: ZPayrixAccount
});
export type BankAccounts = z.infer<typeof ZBankAccounts>;

export type PayrixMerchant = {
    id: PayrixEntity; // unique id for this Merchant
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    lastActivity: string; // date and time of last activity
    entity: PayrixEntity; // id of the Login that last modified the entity
    dba: string; // doing business as name
    new: PayrixBool; // 0 = not new , 1 = new
    established: PayrixDateYearMonthDay; // date established YYYYMMDD
    annualCCSales: number; // The value of the annual credit card sales of this Merchant. This field is specified as an integer in cents.
    avgTicket: number; // The value of the average credit card sales of this Merchant. This field is specified as an integer in cents.
    amex: string; // The American Express merchant identifier for this Merchant, if applicable
    discover: string; // The Discover merchant identifier for this Merchant, if applicable
    mcc: string; // The Merchant Category Code (MCC) for this Merchant. Always 8111 for Legal Services.
    status: EMerchantStatus; // The status of the Merchant
    boarded: string; // The date and time on which this Merchant was successfully boarded
    inactive: PayrixBool;
    frozen: PayrixBool;
    environment: EMerchantEnvironment; // The environment which the Merchant is in, if applicable
    visaMvv: string; // Merchant Verification Value. A value assigned by Visa to identify participation in select merchant programs.
    chargebackNotificationEmail: string;
    statusReason: string; // The reason for manual or closed status
    totalApprovedSales: number; // Show the total approved amount to this merchant
    autoBoarded: PayrixBool;
    saqType: string;
    saqDate: string;
    qsa: string;
    letterStatus: PayrixBool;
    letterDate: string;
    tcAttestation: PayrixBool;
    visaDisclosure: PayrixBool;
    disclosureIP: string;
    disclosureDate: string;
    accountClosureReasonCode: string;
    accountClosureReasonDate: string;
    annualCCSaleVolume: number; // The value of the annual credit card sale volume of this Merchant. This field is specified as an integer in cents.
    annualACHSaleVolume: number; // The value of the annual ACH sale volume of this Merchant. This field is specified as an integer in cents.
    riskLevel: ERiskLevel; // The risk level which the Merchant is in, if applicable
    creditRatio: number; // The credit ratio to use while calculating risk factors for credit, if applicable
    creditTimeliness: string; // The credit timeliness for the Merchant, if applicable
    chargebackRatio: number;
    advancedBilling: PayrixBool;
    locationType: string;
    percentKeyed: number; // The merchant percentage of transactions that are Card Not Present (Includes MOTO and eCommerce)
    totalVolume: number; // The expected total volume for all credit card and ACH payments that the merchant will process for the current year
    percentEcomm: number; // This field indicates what percentage are internet originated transactions
    seasonal: PayrixBool; // Indicates if the merchant is a seasonal merchant or operates year-round
    amexVolume: number; // Indicates the Annual AMEX Sales Volume for the outlet
    incrementalAuthSupported: PayrixBool; // This field allows the ability to submit merchant registration files with a MasterCard (MC) Auth Integrity value
    tmxSessionId: string;
    percentBusiness: number; // This field indicates what percentage of the transactions are Business to Business
    applePayActive: PayrixBool;
    applePayStatus: string;
    googlePayActive: PayrixBool;
    passTokenEnabled: PayrixBool;
    naics: number; // The NAICS sector code that accurately describes the industry the business operates in
    naicsDescription: string; // The NAICS sector description matching the above NAICS sector code. If "Other", industry description is prefixed with "Other: "
    expressBatchCloseMethod: 'TimeInitiated' | 'MerchantInitiated'; // This field indicates the batch close method for this merchant applicable to Express platform.
    expressBatchCloseTime: string; // This field indicates the batch close time for this merchant applicable to Express platform
};

export type PlaidInstitution = {
    name: string; // Bank name
    institution_id: string; // Plaid institution ID
    link_session_id: string; // Plaid link session ID
};

export type PlaidAccount = {
    id: string; // Plaid account ID
    name: string; // Account name
    mask: string; // Account mask, e.g. '0000'
    type: string; // Account type, e.g. 'depository'
    subtype: string; // Account subtype, e.g. 'checking' or 'savings'
    verification_status?: string; // Account verification status
};

export type PlaidSuccessMetadata = {
    institution: PlaidInstitution;
    accounts: PlaidAccount[];
    account: PlaidAccount;
};

export enum EChargebackStatusCode {
    Open = 'open',
    Closed = 'closed',
    Lost = 'lost',
}

export const ZChargebackStatusCode = z.enum(EChargebackStatusCode);
export type ChargebackStatusCode = z.infer<typeof ZChargebackStatusCode>;

export const ZPayrixChargebackMessage = ZPayrixEntityHeader.extend({
    chargeback: z.string(), // The identifier of the Chargeback resource that this chargebackMessage relates to.
    date: ZPayrixDateYearMonthDay, // The date of this chargebackMessage. The date is specified as an eight digit string in YYYYMMDD format, for example, '20160120' for January 20, 2016.
    type: ZChargebackMessageType, // The type of this chargebackMessage.
    fromQueue: z.string().optional(),
    toQueue: z.string().optional(),
    contact: z.email('Not a valid email').optional(), // The identifier of the Contact for this chargebackMessage.
    amount: z.number().optional(), // The amount that this chargebackMessage corresponds to. For example, if the 'type' is set to '3' (Accept Liability), then this amount indicates that the liability should be for this amount. This field is specified as an integer in cents.
    currency: ZPayrixCurrency, // (USD/CAN)
    note: z.string().optional(), // A free-text note relating to this chargebackMessage.
    status: ZChargebackMessageStatus.optional(), // the current status of the chargeback
    imported: ZPayrixBool.optional(),
});
export type PayrixChargebackMessage = z.infer<typeof ZPayrixChargebackMessage>;

export const ZPayrixChargebackMessageResult = ZPayrixEntityHeader.extend({
    chargebackMessage: ZPayrixEntity, // The identifier of the chargebackMessage resource that this chargebackMessageResult relates to.
    type: ZChargebackMessageResultType,
    message: z.string().optional(),
});
export type PayrixChargebackMessageResult = z.infer<typeof ZPayrixChargebackMessageResult>;

export const ZPayrixChargebackStatus = ZPayrixEntityHeader.extend({
    chargeback: ZPayrixEntity,
    chargebackMessage: ZPayrixEntity,
    status: ZChargebackMessageStatus
});
export type PayrixChargebackStatus = z.infer<typeof ZPayrixChargebackStatus>;

export const ZPayrixChargebackDocument = ZPayrixEntityHeader.extend({
    chargeback: ZPayrixEntity,
    ref: ZPayrixEntityWithSuffix,
    type: ZPayrixDocumentType,
    name: z.string(),
    description: z.string().optional(),
    status: ZChargebackDocumentStatus,
    documentSource: ZChargebackDocumentSource.optional(),
});
export type PayrixChargebackDocument = z.infer<typeof ZPayrixChargebackDocument>;

export type PlaidEventMetadata = {
    event_type: string; // Event type, e.g. 'ERROR'
    error_code: string; // Error code, e.g. 'INVALID_CREDENTIALS'
    error_message: string; // Error message
    exit_status: string; // Exit status, e.g. 'requires_questions'
    institution_id: string; // Plaid institution ID
    institution_name: string; // Plaid institution name
    institution_search_query: string; // Plaid institution search query
    mfa_type: string; // MFA type, e.g. 'questions'
    view_name: string; // View name, e.g. 'ERROR'
    request_id: string; // Plaid request ID
    link_session_id: string; // Plaid link session ID
    timestamp: string; // Timestamp
    selection: string; // Selection
};

export type PlaidExitError = {
    error_type: string; // Error type, e.g. 'ITEM_ERROR'
    error_code: string; // Error code, e.g. 'INVALID_CREDENTIALS'
    error_message: string; // Error message
    display_message: string; // Display message
};

export type PlaidExitMetadata = {
    institution: PlaidInstitution;
    status: string; // Exit status, e.g. 'requires_questions'
    link_session_id: string; // Plaid link session ID
    request_id: string; // Plaid request ID
};

/**
 * @enum {string}
 * @readonly
 */
export enum EPayrixEntityType {
    ACCOUNTS = 'accounts',
    CUSTOMERS = 'customers',
    ENTITIES = 'entities',
    FUNDS = 'funds',
    MEMBERS = 'members',
    MERCHANTS = 'merchants',
    ORGS = 'orgs',
    PAYOUTS = 'payouts',
    PLANS = 'plans',
    SUBSCRIPTIONS = 'subscriptions',
    TEAM_LOGINS = 'team_logins',
    TOKENS = 'tokens',
    TXNS = 'txns',
    ENTITY_RESERVES = 'entityReserves',
    FEE_RULES = 'feeRules',
    FEES = 'fees',
    ORG_ENTITIES = 'orgEntities',
    RESERVE_ENTRIES = 'reserveEntries',
    RESERVES = 'reserves',
    VENDORS = 'vendors',
    ACCOUNT_VERIFICATIONS = 'accountVerifications',
    ADJUSTMENTS = 'adjustments',
    AGGREGATION_RESULT_GROUPS = 'aggregationResultGroups',
    AGGREGATION_RESULTS = 'aggregationResults',
    AGGREGATIONS = 'aggregations',
    ALERT_ACTIONS = 'alertActions',
    ALERTS = 'alerts',
    ALERT_TRIGGERS = 'alertTriggers',
    API_KEYS = 'apikeys',
    ASSESSMENTS = 'assessments',
    AUTH_TOKENS = 'authTokens',
    BATCHES = 'batches',
    BILLING_EVENTS = 'billingEvents',
    BILLING_MODIFIERS = 'billingModifiers',
    BILLINGS = 'billings',
    BINS = 'bins',
    CHANGE_REQUESTS = 'changeRequests',
    CHARGEBACK_REQUESTS = 'chargebackRequests',
    CHARGEBACK_DOCUMENTS = 'chargebackDocuments',
    CHARGEBACK_MESSAGE_RESULTS = 'chargebackMessageResults',
    CHARGEBACK_MESSAGES = 'chargebackMessages',
    CHARGEBACKS = 'chargebacks',
    CHARGEBACK_STATUSES = 'chargebackStatuses',
    CONFIRM_CODES = 'confirmCodes',
    CONTACTS = 'contacts',
    DECISION_ACTIONS = 'decisionActions',
    DECISION_RULES = 'decisionRules',
    DISBURSEMENT_ENTRIES = 'disbursementEntries',
    DISBURSEMENT_REFS = 'disbursementRefs',
    DISBURSEMENTS = 'disbursements',
    DISBURSEMENT_RESULTS = 'disbursementResults',
    DIVISIONS = 'divisions',
    ENTITY_DATAS = 'entityDatas',
    ENTITY_DEBTS = 'entityDebts',
    ENTITY_RETURNS = 'entityReturns',
    ENTRIES = 'entries',
    ENTRY_ORIGINS = 'entryOrigins',
    EXTERNAL_FEES = 'externalFees',
    FEE_MODIFIERS = 'feeModifiers',
    FUND_ORIGINS = 'fundOrigins',
    HOLDS = 'holds',
    INVOICE_ITEMS = 'invoiceItems',
    INVOICE_LINE_ITEMS = 'invoiceLineItems',
    INVOICE_PARAMETERS = 'invoiceParameters',
    INVOICE_RESULTS = 'invoiceResults',
    INVOICES = 'invoices',
    IP_LISTS = 'iplists',
    ITEMS = 'items',
    LOGINS = 'logins',
    MESSAGES = 'messages',
    MESSAGE_THREADS = 'messageThreads',
    MERCHANT_RESULTS = 'merchantResults',
    MAPPINGS = 'mappings',
    NOTES = 'notes',
    NOTE_DOCUMENTS = 'noteDocuments',
    ORG_FLOW_ACTIONS = 'orgFlowActions',
    ORG_FLOWS = 'orgFlows',
    PAYMENT_UPDATE_GROUPS = 'paymentUpdateGroups',
    PAYMENT_UPDATES = 'paymentUpdates',
    PAYOUT_FLOWS = 'payoutFlows',
    PENDING_ENTRIES = 'pendingEntries',
    PROFIT_SHARE_RESULTS = 'profitShareResults',
    PROFIT_SHARE_RULES = 'profitShareRules',
    PROFIT_SHARES = 'profitShares',
    REFUNDS = 'refunds',
    REPORT_ITEMS = 'reportItems',
    REPORT_RESULTS = 'reportResults',
    REPORTS = 'reports',
    SECRETS = 'secrets',
    SESSIONS = 'sessions',
    STATEMENT_ENTRIES = 'statementEntries',
    STATEMENTS = 'statements',
    SUBSCRIPTION_TOKENS = 'subscriptionTokens',
    TEAMS = 'teams',
    TERMINALS = 'terminals',
    TERMINAL_TXN_DATAS = 'terminalTxnDatas',
    TERMINAL_TXN_METADATAS = 'terminalTxnMetadatas',
    TERMINAL_TXN_RESULTS = 'terminalTxnResults',
    TERMINAL_TXNS = 'terminalTxns',
    TXN_DATAS = 'txnDatas',
    TXN_METADATAS = 'txnMetadatas',
    TXN_RESULTS = 'txnResults',
    TXN_SESSIONS = 'txnSessions',
    VALUE_ADDED_SERVICES_FLOWS = 'valueAddedServicesFlows',
    VALUE_ADDED_SERVICES = 'valueAddedServices'
}

export const ZPayrixEntityType = z.enum(EPayrixEntityType);
export type PayrixEntityType = z.infer<typeof ZPayrixEntityType>;

export const ZPayrixAddress = z.object({
    type: z.string().optional().nullable(),
    address1: z.string().optional().nullable(),
    address2: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    zip: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
});
export type PayrixAddress = z.infer<typeof ZPayrixAddress>;

export const ZOutlawAddress = z.object({
    street: z.string().optional(),
    unit: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string(),
    country: z.string().optional(),
});

export enum EAccountReserved {
    NoReserve = 0,
    AccountWithheld = 1,
    AccountUsagePendingManualReview = 3,
    MoveAllFundsFromThisAccountIntoAReserve = 4
}

export const ZAccountReserved = z.enum(EAccountReserved);
export type AccountReserved = z.infer<typeof ZAccountReserved>;

export enum ESubscriptionOrigin {
    eCommerce = 2,
    MailOrTelephoneOrder = 3,
    Successful3DS = 5,
    Attempted3DS = 6,
    Payframe = 8
}

export const ZSubscriptionOrigin = z.enum(ESubscriptionOrigin);
export type SubscriptionOrigin = z.infer<typeof ZSubscriptionOrigin>;

export enum ETimeZone {
    EasternStandardTime = 'est',
    CentralStandardTime = 'cst',
    PacificStandardTime = 'pst',
    MountainStandardTime = 'mst',
    AlaskaStandardTime = 'akst',
    HawaiiStandardTime = 'hst',
    SingaporeStandardTime = 'sst',
    ChamorroStandardTime = 'chst',
    AtlanticStandardTime = 'ast',
    PWT = 'pwt',
    MHT = 'mht',
    CHUT = 'chut',
    NST = 'nst'
}

export const ZTimeZone = z.enum(ETimeZone);
export type TimeZone = z.infer<typeof ZTimeZone>;

export enum ETaxIdStatus {
    Pending = 0,
    Valid = 1,
    Invalid = 2,
    NotRequired = 3
}

export const ZTaxIdStatus = z.enum(ETaxIdStatus);
export type TaxIdStatus = z.infer<typeof ZTaxIdStatus>;

export enum EPayoutSchedule {
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
    Annually = 4,
    Single = 5
}

export const ZPayoutSchedule = z.enum(EPayoutSchedule);
export type PayoutSchedule = z.infer<typeof ZPayoutSchedule>;

export enum ETerminalCapability {
    KEY_ENTRY_ONLY = 1,
    MAGNETIC_STRIPE = 2,
    INTEGRATED_CIRCUIT_READER = 3
}

export const ZTerminalCapability = z.enum(ETerminalCapability);
export type TerminalCapability = z.infer<typeof ZTerminalCapability>;

export enum EEntryMode {
    TERMINAL_USED_WITH_KEY = 1,
    TRACK1_READ = 2,
    TRACK2_READ = 3,
    COMPLETE_MAGNETIC_STRIPE_READ = 4,
    EMV_CHIP_READ = 5
}

export const ZEntryMode = z.enum(EEntryMode);
export type EntryMode = z.infer<typeof ZEntryMode>;

export enum ETransactionOrigin {
    Terminal = 1,
    Ecommerce = 2,
    MailOrTelephoneOrder = 3,
    ApplePay = 4,
    Success3DS = 5,
    Attempted3DS = 6,
    Recurring = 7,
    Payframe = 8
}

export const ZTransactionOrigin = z.enum(ETransactionOrigin);
export type TransactionOrigin = z.infer<typeof ZTransactionOrigin>;

export enum EMerchantStatus {
    NotReady = 0,
    Ready = 1,
    Boarded = 2,
    Manual = 3,
    Closed = 4,
    Incomplete = 5,
    Pending = 6
}

export const ZMerchantStatus = z.enum(EMerchantStatus);
export type MerchantStatus = z.infer<typeof ZMerchantStatus>;

export enum EMerchantEnvironment {
    Supermarket = 'supermarket',
    MailOrTelephoneOrder = 'moto',
    CardPresent = 'cardPresent',
    Fuel = 'fuel',
    ServiceStation = 'serviceStation',
    Restaurant = 'restaurant',
    Ecommerce = 'eCommerce'
}

export const ZMerchantEnvironment = z.enum(EMerchantEnvironment);
export type MerchantEnvironment = z.infer<typeof ZMerchantEnvironment>;

export enum ERiskLevel {
    Restricted = 'restricted',
    Prohibited = 'prohibited',
    High = 'high',
    Medium = 'medium',
    Low = 'low'
}

export const ZRiskLevel = z.enum(ERiskLevel);
export type RiskLevel = z.infer<typeof ZRiskLevel>;

// export enum ETransactionTarget {
//     Operating = 'Operating',
//     Trust = 'TRUST'
// }
//
// export const ZTransactionTarget = z.enum(ETransactionTarget);
// export type TransactionTarget = z.infer<typeof ZTransactionTarget>;

export enum EPayoutUnit {
    Percent = 1,
    Actual = 2,
    PercentNegative = 3
}

export const ZPayoutUnit = z.enum(EPayoutUnit);
export type PayoutUnit = z.infer<typeof ZPayoutUnit>;

export enum EScheduleUnit {
    Percent = 'percent',
    Actual = 'actual'
}

export const ZScheduleUnit = z.enum(EScheduleUnit);
export type ScheduleUnit = z.infer<typeof ZScheduleUnit>;

export enum EFeeUnit {
    Percent = 1,
    Fixed = 2,
    Surcharge = 3
}

export const ZFeeUnit = z.enum(EFeeUnit);
export type FeeUnit = z.infer<typeof ZFeeUnit>;

export enum EPayoutSkipoffs {
    DoNotSkipHolidaysAndWeekends = 0,
    SkipHolidaysAndWeekends = 1
}

export const ZPayoutSkipoffs = z.enum(EPayoutSkipoffs);
export type PayoutSkipoffs = z.infer<typeof ZPayoutSkipoffs>;

export enum EDisbursementCode {
    Pending = 'pending',
    Internal = 'internal',
    NSF = 'nsf',
    BadAccount = 'badAccount',
    Unauthorized = 'unauthorized',
    General = 'general',
    NOC = 'noc',
    Parameter = 'parameter',
    SameDay = 'sameDay',
    TransferDetails = 'transferDetails',
    Platform = 'platform'
}

export const ZDisbursementCode = z.enum(EDisbursementCode);
export type DisbursementCode = z.infer<typeof ZDisbursementCode>;

export enum EDisbursementEvent {
    Days = 1, // DisbursementEntry for an Entry that triggers every day.
    Weeks = 2, // DisbursementEntry for an Entry that triggers every week.
    Months = 3, // DisbursementEntry for an Entry that triggers every month.
    Years = 4, // DisbursementEntry for an Entry that triggers every year.
    Single = 5, // DisbursementEntry for an Entry related to a one-off event.
    Auth = 6, // DisbursementEntry for an Entry related to the time of authorization of a transaction.
    Capture = 7, // DisbursementEntry for an Entry related to the capture time of a Transaction.
    Refund = 8, // DisbursementEntry for an Entry related to when a refund transaction is processed
    Board = 9, // DisbursementEntry for an Entry related to when the Merchant is boarded.
    Payout = 10, // DisbursementEntry for an Entry related to when a payout is processed.
    Chargeback = 11, // DisbursementEntry for an Entry related to when a card chargeback occurs
    Overdraft = 12, // DisbursementEntry for an Entry related to when an overdraft usage charge from a bank is levied.
    Interchange = 13, // DisbursementEntry for an Entry related to when interchange Fees are assessed for the Transactions of this Merchant.
    Processor = 14, // DisbursementEntry for an Entry related to when the Transactions of this Merchant are processed by a payment processor.
    AchFail = 15, // DisbursementEntry for an Entry related to when an automated clearing house failure occurs.
    Account = 16, // DisbursementEntry for an Entry related when a bank account is verified.
    SIFT = 17, // DisbursementEntry for an Entry related to Transaction fraud score.
    Adjustment = 18, // DisbursementEntry for an Entry related to adjusted funds.
    Retrieval = 19, // DisbursementEntry for an Entry related to Retrieval Request Chargeback.
    Arbitration = 20, // DisbursementEntry for an Entry related to Arbitration Chargeback.
    ECheckSale = 21, // DisbursementEntry for an Entry related to an eCheck Sale.
    ECheckRefund = 22, // DisbursementEntry for an Entry related to an eCheck Refund.
    ECheckReturn = 23, // DisbursementEntry for an Entry related to an eCheck Return.
    Settlement = 24, // DisbursementEntry for an Entry related to transaction settlement.
    Misuse = 25, // DisbursementEntry for an Entry related to Misuse of authorization.
    ProfitShare = 26, // DisbursementEntry for an Entry related to a profit sharing entry.
    Unauthorized = 27, // DisbursementEntry for an Entry related to an unauthorized entry.
    ACHNotificationOfChange = 28, // DisbursementEntry for an Entry related to an ACH Notification of Change.
    ECheckNotificationOfChange = 29, // DisbursementEntry for an Entry related to an eCheck Notifications of Change.
    ECheckFail = 30, // DisbursementEntry for an Entry related to an eCheck Fail
    ECheckNSF = 31, // DisbursementEntry for an Entry related to an eCheck Non-Sufficent Funds.
    CurrencyConversion = 32, // DisbursementEntry for an Entry related to a currency conversion.
    TerminalTxn = 33, // DisbursementEntry for an Entry related to a terminal transaction.
    ReversePayout = 34, // DisbursementEntry for an Entry related to a payout thats been reversed.
    PartialReversePayout = 35, // DisbursementEntry for an Entry related to a payout thats been partially reversed.
    ReserveEntry = 36, // DisbursementEntry for an Entry related to reserved funds.
    ReserveEntryRelease = 37, // DisbursementEntry for an Entry related to released reserves.
    PendingEntry = 38, // DisbursementEntry for an Entry related to pending funds.
    PendingPaid = 39, // DisbursementEntry for an Entry related to paid pending funds.
    Remainder = 40, // DisbursementEntry for an Entry on the remaining, non-disbursed funds.
    RemainderUsed = 41, // DisbursementEntry for an Entry related to previously non-disbursed funds.
    PendingRefundCancelled = 42 // DisbursementEntry for an Entry related pending refund that was cancelled.
}

export const ZDisbursementEvent = z.enum(EDisbursementEvent);
export type DisbursementEvent = z.infer<typeof ZDisbursementEvent>;

export enum EEventType {
    Days = 1, // The Pending Entry is triggered by a daily event
    Weeks = 2, // The Pending Entry is triggered by a weekly event
    Months = 3, // The Pending Entry is triggered by a monthly event
    Years = 4, // The Pending Entry is triggered by an annual event
    Single = 5, // The Pending Entry is a one-off event
    Authorization = 6, // The Pending Entry is triggered at the time of authorization of a transaction
    Capture = 7, // The Pending Entry is triggered at the capture time of a Transaction
    Refund = 8, // The Pending Entry is triggered when a refund transaction is processed
    Boarded = 9, // The Pending Entry is triggered when the Merchant is boarded
    Payout = 10, // The Pending Entry is triggered when a payout is processed
    Chargeback = 11, // The Pending Entry is triggered when a card chargeback occurs
    Overdraft = 12, // The Pending Entry is triggered when an overdraft usage charge from a bank is levied
    Interchange = 13, // The Pending Entry is triggered when interchange Fees are assessed for the Transactions of this Merchant
    Processor = 14, // The Pending Entry is triggered when the Transactions of this Merchant are processed by a payment processor
    ACHFail = 15, // The Pending Entry is triggered when an automated clearing house failure occurs
    AccountVerification = 16, // The Pending Entry is triggered when a bank account is verified
    SIFT = 17, // The Entry is triggered by a transaction's fraud score
    Adjustment = 18, // The Entry is triggered when there's an adjustment
    Retrieval = 19, // Retrieval Request Chargeback
    Arbitration = 20, // Arbitration Chargeback
    ECheckSale = 21, // The Pending Entry is triggered by an eCheck sale transaction
    ECheckRefund = 22, // The Pending Entry is triggered by an eCheck refund
    ECheckReturn = 23, // The Pending Entry is triggered by an eCheck return
    Settlement = 24, // The Pending Entry is triggered when a transaction has been settled
    Misuse = 25, // The Pending Entry is triggered when there's been a Misuse of authorization
    ProfitShare = 26, // The Entry is triggered by a profit sharing entry event
    Unauthorized = 27, // The Entry is triggered by an unauthorized entry event
    AchNotificationOfChange = 28, // The Entry is triggered by an ACH Notification of Change
    ECheckNotificationOfChange = 29, // The Entry is triggered by an eCheck Notification of Change
    ECheckFail = 30, // The Entry is triggered by an eCheck Fail
    ECheckNonSufficientFunds = 31, // The Entry is triggered by an eCheck Non-Sufficient Funds
    CurrencyConversion = 32, // This Entry is triggered by a currency conversion
    TerminalTransaction = 33, // This Entry is triggered by a terminal transaction
    ReversePayout = 34, // This Entry is triggered by a payout that's been reversed
    PartialReversePayout = 35, // This Entry is triggered by a payout that's been partially reversed
    PaymentCheck = 43, // This Entry is triggered by a payment check for Account Updater
    PaymentUpdate = 44, // This Entry is triggered by a payment update for Account Updater
    PaymentGroupCheck = 45, // This Entry is triggered by a payment group check for Account Updater
    PaymentGroupUpdate = 46, // This Entry is triggered by a payment group update for Account Updater
    EntryRefund = 47 // This Entry is triggered by an entry refund
}

export const ZEventType = z.enum(EEventType);
export type EventType = z.infer<typeof ZEventType>;

// ZFeeType, ZFeeSchedule, ZFeeCollection
export enum EFeeType {
    Fee = 1,
    Assessment = 2
}

export const ZFeeType = z.enum(EFeeType);
export type FeeType = z.infer<typeof ZFeeType>;

export enum EFeeCollection {
    Transaction = 1, // The total amount of all txns
    TransactionTaxId = 2, // The total amount of all txns per entity ein/tax id
    TransactionMerchant = 3, // The total amount of all txns per entity
}

export const ZFeeCollection = z.enum(EFeeCollection);
export type FeeCollection = z.infer<typeof ZFeeCollection>;

export enum EFeeSchedule {
    Days = 1, // Days
    Weeks = 2, // Weeks
    Months = 3, // Months
    Years = 4, // Years
    Single = 5, // Single
    Auth = 6, // Auth
    Capture = 7, // Capture
    Refund = 8, // Refund
    Board = 9, // Boarding
    Payout = 10, // Merchant Payout
    Chargeback = 11, // Chargeback fees for FIRST, REPRESENTMENT, PRE_ARBITRATION
    Overdraft = 12, // Overdraft
    Interchange = 13, // Interchange
    Processor = 14, // Processor
    AchFail = 15, // ACH Failure
    Account = 16, // Account Fee
    SIFT = 17, // SIFT fraud detection
    Adjustment = 18, // Adjustment
    Retrieval = 19, //  Retrieval Request Chargeback
    Arbitration = 20, // Arbitration Chargeback
    ECheckSale = 21, // ECSALE transaction
    ECheckCRefund = 22, // ECREFUND transaction
    ECheckReturn = 23, // ECRETURN returns
    Settlement = 24, // Txn settlement (legacy)
    Misurse = 25, // Misuse of authorization
    ProfitShare = 26, // Profit Sharing entry event
    Unauth = 27, // Unauth transaction
    AchNotificationOfChange = 28, // ACH disbursement NOC
    ECheckNotificationOfChange = 29, // eCheck transaction NOC
    ECheckFail = 30, // eCheck transaction failure return
    ECheckNotSufficientFunds = 31, // eCheck transaction nsf return
    CurrencyConversion = 32, // Currency conversion fee
    TerminalTxn = 33, // TerminalTxns fee
    ReversePayout = 34, // Reverse payout fee
    PartialReversePayout = 35, //  Partial reverse payout fee
    PaymentUpdate = 44, //  Single payment update
    PaymentGroupUpdate = 46, //  Group payment update
    EntryRefund = 47, //  Entry refunds
    Statement = 51, //  Payment of statement
    MerchantCreation = 52, //  Merchant fee upon creation
    RealtimeBusinessSearch = 53, //  Real time business search
    RealtimeMemberSearch = 54, //  Real time member search
    MastercardMatch = 55, //  Mastercard match
    BusinessInstantId = 56, //  Business instant id
    ConsumerInstantId = 57, //  Consumer instant id
    ThreatMatrix = 58, //  Threat metrix
    LegitScriptRegister = 59, //  Legit script register
    EquifaxConsomerReport = 60, //  Equifax consumer report
    GuideStart = 61, // Guidestar
    PayloadAttribute = 62, //  Internal Decision V2
    TinCheck = 63, // Tin check
    EquifaxCommercialReport = 64, //  Equifax commercial report
    LegitScriptCheckMerchant = 65, //  Legit script check merchant
    Plaid = 66, // Plaid
    StatementReversal = 67, //  Reversal of statement
    GiactECheck = 68, // GIACT call to verify creator for ECheck bank Account
    GiactBankAccount = 69, //  GIACT call to verify merchant settlement account on sign up or post boarding account
    BoardDecision = 70, //  Process decision fee after board
    TransactionRiskDecision = 71, //  Txn going through a risk decision
    FixedAquirerNetworkFee = 72, // External Fees
    MastercardMerchantLocation = 73, // External Fees
    VisaIntegrity = 74, // External Fees
    SaferPaymentsBasic = 75, // External Fees
    SaferPaymentsManaged = 76, // External Fees
    SaferPaymentPciNonValidation = 77, //  External Fees
    OmnitokensVolume = 78, // External Fees
    PayoutReturn = 79, // Payout Return
    PayoutPartialReturn = 80, // Payout Partial Return
    RevenueShare = 81, // Rev share
    CardSettlement = 82, // Txns card settlement
    ECheckSettlement = 83, // Txns e-check settlement
    RevenueShareCard = 84, // Rev Share Schedules
    RevenueShareECheck = 85, // Rev Share Schedules
    RevenueShareDBM = 86, // Rev Share Schedules
    PlaidIdentityMatch = 88, // Plaid
    TransactionPlaidIdentityMatch = 89, // Plaid
    PlaidGetIdentity = 90, // Plaid
    TransactionPlaidGetIdentity = 91, // Plaid
    PlaidGetAuth = 92, // Plaid
    TransactionPlaidGetAuth = 93, // Plaid
    ValutecEssentialGift = 200, // External Fees
    ValutecEssentialMonthlyTransaction = 201, // External Fees
    ValutecDigitalGiftPlusPackage = 202, // External Fees
    ValutecDigitalGiftPlusPackageTransaction = 203, // External Fees
    ValutecLoyaltyPlusPackage = 204, // External Fees
    ValutecLoyaltyPlusPackageTransaction = 205, // External Fees
    ValutecTransactionFee = 206, // External Fees
    ValutecSetupFee = 207, // External Fees
    ValutecGiftAchPooling = 208, // External Fees
    ValutecJumpStartKit = 209, // External Fees
    ValutecLaunchBoxKit = 210, // External Fees
    Valutec500CustomCards = 211, // External Fees
    ValutecMaintenanceFee = 212, // External Fees
    ValutecDigitalGiftPlusPackageMe = 213, // External Fees
    TransactionThreatMetrix = 402, // External Fees
    ThreatMetrixMailage = 403, // External Fees
    ThreatMetrixFraudPoint = 404, // Threat Metrix Fraud Point
    GiactInquiry = 405, // GIACT Inquiry
    GiactTransactionGIdenify = 406, // GIACT gIDENTIFY service on a transaction
    TruliooIdentificationVerification = 407, // Trulioo Identification Verification
    TruliooAntiMoneyLaunderingVerificaiton = 408, // Trulioo Anti-Money Laundering Verificaiton
    TruliooBusinessVerification = 409, // External Fees
    ThreatMetrixPhoneFinder = 410, // External Fees
}

export const ZFeeSchedule = z.enum(EFeeSchedule);
export type FeeSchedule = z.infer<typeof ZFeeSchedule>;

export const ZDateRange = z.object({
    startDate: z.date(),
    endDate: z.date(),
});
export type DateRange = z.infer<typeof ZDateRange>;

export const ZPaymentCard = z.object({
    nameOnCard: z.string(),
    cardNumber: z.string(),
    expirationMonth: z.string(),
    expirationYear: z.string(),
    cvv: z.string(),
});
export type PaymentCard = z.infer<typeof ZPaymentCard>;

export enum EBankAccountHolderType {
    Individual = 'Individual',
    Business = 'Business',
}

export const ZBankAccountHolderType = z.enum(EBankAccountHolderType);
export type BankAccountHolderType = z.infer<typeof ZBankAccountHolderType>;

export enum EBankAccountTypeUppercase {
    Checking = 'CHECKING',
    Savings = 'SAVINGS',
}

export enum EBankAccountType {
    checking = 'checking', // Used by Lawpay
    Checking = 'Checking', // What we get back from Payrix
    CHECKING = 'CHECKING', // What we somehow turned it into
    savings = 'savings', // Used by Lawpay
    Savings = 'Savings', // What we get back from Payrix
    SAVINGS = 'SAVINGS', // What we somehow turned it into
}

export const ZBankAccountType = z.enum(EBankAccountType);
export type BankAccountType = z.infer<typeof ZBankAccountType>;

export const ZPaymentCheck = z.object({
    accountHolderType: ZBankAccountHolderType,
    accountType: ZBankAccountType,
    firstName: z.string(),
    lastName: z.string(),
    routingNumber: z.string(),
    accountNumber: z.string(),
});
export type PaymentCheck = z.infer<typeof ZPaymentCheck>;

/**
 * This structure is used with the payments page and unportalHandler routes.
 */
export const PaymentRequest = z.object({
    firmId: ZUuid,
    addedOn: z.number(),
    txTypes: z.array(ZTransactionType),
    payerId: ZUuid,
    clientProcessorId: ZUuid,
    matterId: ZUuid,
    clientIds: z.array(ZUuid),
    card: ZPaymentCard.optional(),
    check: ZPaymentCheck.optional(),
    address: ZPayrixAddress,
    email: z.string(),
    invoiceAmountDue: z.number().optional(),
    trustAmountDue: z.number().optional(),
    nickname: z.string().optional(),
    paymentId: z.string().optional(),
});
export type PaymentRequest = z.infer<typeof PaymentRequest>;

/**
 * Payrix Entity Structure.
 *
 * An Entity is the top-level resource of a business in the API.
 * It describes the details of that business in its fields.
 * Each Entity can describe either a Merchant or a Facilitator (payment facilitator).
 */
export type Entity = {
    id: string; // unique id for this Entity
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the user that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    ipCreated: string; // The incoming ip address from which this Entity was created.
    ipModified: string; // The incoming ip address from which this Entity was last modified.
    clientIp: string; // The client ip address from which the Entity was created. Valid values are any Ipv4 or Ipv6 address.
    login: PayrixEntity; // The id of the Login resource that owns this Entity
    parameter?: string; // The parameter associated with this Entity
    type: EMerchantType; // The type of entity (company structure)
    name: string; // Name of the Entity (1-100 characters)
    address1: string; // The first line of the address associated with this Entity
    address2: string; // The second line of the address
    city: string; // The city associated with the address
    state: string; // Two-letter code for a state or province
    zip: string; // ZIP/Postal code as a string
    country: string; // USA or CAN. Currently only supports USA.
    timezone: ETimeZone; // the timezone of the entity (use codes above)
    phone: string; // primary phone number for this entity
    fax: string; // fax number for this entity
    email: string; // email for this entity
    website: string; // website for this entity
    ein: string; // the EIN associated with this entity (should be a 9-digit number)
    tcVersion: string; // An indicator showing the version of the terms and conditions that this Entity has accepted
    tcDate: PayrixDateYearMonthDayHourMinute; // Date the tcVersion was last updated (YYYYMMDDHHmm)
    tcIp: IpAddress; // IP address of client from last tcVersion update
    tcAcceptDate: PayrixDateYearMonthDayHourMinute; // Date and time on which this Entity accepted the Terms and Conditions, if different than tcDate (YYYYMMDDHHmm)
    tcAcceptIp: IpAddress; // IP address from which this Entity accepted the terms and conditions, if different from tcIp.
    custom: string; // Custom, free-form field for client-supplied text, 0-1000 characters long. This is where we can store our Id
    inactive: EAccountInactive; // current active/inactive state,
    frozen: EAccountFrozen; // frozen/unfrozen state
    tinStatus: ETaxIdStatus; // The Tax ID (TIN) status of the entity
    checkStage: ECheckStage; // the last stage completed for risk
    reserved: EAccountReserved; // reserve status of the entity
    public: PayrixBool; // 0 for private, 1 for public
    customerPhone: string; // customer-facing phone number
    locations: number; // number of locations
    industry: string;
    displayName: string; // display name for this entity
    totalCreditDisbursements: number;
    secondary: string; // secondary billing descriptor to appear on the bank statements for funds transfer for the entity
    einType: 'ssn' | 'tin' | 'other'; // Indicates if the EIN being used is a TIN or SSN or "other/unknown" number
    irsFilingName: string; // the legal name of the company
    currency: string; // 3-letter currency name, e.g. USD
    appleDomain: string; // the website URL associated with this entity
    globalBusinessId: string; // business registration number, 9-10 characters
    globalBusinessType: 'ssn' | 'tin' | 'other'; // not sure how this is different from einType
    pendingRiskCheck: 'pending' | 'successful'; // whether it is a pending risk check
};

// Customer
export const ZPayrixCustomer = ZPayrixEntityHeader.extend({
    merchant: ZPayrixEntity, // The id of the Merchant (not the Merchant's entity)
    first: z.string().optional().nullable(), // first name
    middle: z.string().optional().nullable(), // middle name
    last: z.string(), // last name
    company: z.string().optional().nullable(), // company
    phone: z.string().optional().nullable(), // primary phone number for this entity
    fax: z.string().optional().nullable(), // fax number for this entity
    email: z.email('Not a valid email').optional(), // email for this entity
    address1: z.string().optional().nullable(), // The first line of the address associated with this Entity
    address2: z.string().optional().nullable(), // The second line of the address
    city: z.string().optional().nullable(), // The city associated with the address
    state: z.string().optional().nullable(), // Two-letter code for a state or province
    zip: z.string().optional().nullable(), // ZIP/Postal code as a string
    country: z.string().optional().nullable(), // USA or CAN. Currently only supports USA
    shippingFirst: z.string().optional().nullable(),
    shippingMiddle: z.string().optional().nullable(),
    shippingLast: z.string().optional().nullable(),
    shippingCompany: z.string().optional().nullable(),
    shippingAddress1: z.string().optional().nullable(), // The first line of the address associated with this Entity
    shippingAddress2: z.string().optional().nullable(), // The second line of the address
    shippingCity: z.string().optional().nullable(), // The city associated with the address
    shippingState: z.string().optional().nullable(), // Two-letter code for a state or province
    shippingZip: z.string().optional().nullable(), // ZIP/Postal code as a string
    shippingCountry: z.string().optional().nullable(), // USA or CAN. Currently only supports USA
    shippingPhone: z.string().optional().nullable(), // primary phone number for this entity
    shippingFax: z.string().optional().nullable(), // fax number for this entity
    custom: z.string().optional().nullable(), // Custom, free-form field for client-supplied text, 0-1000 characters long. This is where we can store our Id
    authTokenCustomer: z.string().optional().nullable(),
    entity: ZPayrixEntity.optional().nullable(), // the entityId of the entity that owns this account
});

export type PayrixCustomer = z.infer<typeof ZPayrixCustomer>;


// PaymentInfo

export const ZPaymentInfo = z.object({
    id: z.string(),
    method: ZPaymentMethods,
    number: z.string(),
    routing: z.string().optional().nullable(),
    bin: z.string().optional().nullable(),
    last4: z.string().optional().nullable(),
    cvv: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    expiration: MonthYearAsMMYY,
    phone: z.string().optional().nullable(),
    email: z.email('Not a valid email').optional().nullable(),
    address: z.string().optional().nullable()
});
export type PaymentInfo = z.infer<typeof ZPaymentInfo>;

// export type PaymentInfo = {
//     method: PaymentMethods; // @see PaymentMethods
//     number: string; // account number or card number
//     routing?: string; // routing number (ACH only)
//     cvv?: string; // CVV code (CC only)
//     expiration?: MonthYearAsMMYY; // MMYY format
//     phone?: string;
//     email?: string;
//     address?: any;
// };

/**
 * @enum {number}
 * @readonly
 */
export enum ScheduleFactor {
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
    Annually = 4
}

/**
 * @enum {string}
 * @readonly
 */
export enum PlanType {
    Recurring = 'recurring',
    Installment = 'installment'
}


// A payment plan, used to create a subscription.
export type PayrixPlan = {
    id: PayrixEntity; // unique id for this Merchant
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    merchant: PayrixEntity; // The id of the Merchant that owns this Plan
    name: string; // The name of the Plan (1-100 characters)
    description: string; // The description of the Plan (1-100 characters)
    schedule: ScheduleFactor; // The schedule that determines when the subscription related to this Plan is triggered
    scheduleFactor: number; // A multiplier that you can use to adjust the schedule set in the 'schedule' field, such as daily, weekly, monthly, or annually. This field is specified as an integer and its value determines how the interval is multiplied.
    amount: number; // The amount of each payment in cents
    inactive: PayrixBool; // Whether this resource is marked as inactive
    frozen: PayrixBool; // Whether this resource is marked as frozen
    maxFailures: number;
    type: PlanType; // The type of the Plan
    txnDescription: string; // The description of the Txn that will be created through this Subscription.
    order: PayrixEntity; // The order of the Txn that will be created through this Subscription.
    billing: PayrixEntity;
    um: EScheduleUnit; // 'actual' or 'percentage'
};

export type PayrixLogin = {
    id: PayrixEntity; // unique id for this Login
    created?: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified?: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator?: PayrixEntity; // id of the Login that created the entity
    modifier?: PayrixEntity; // id of the Login that last modified the entity
    login: string, // the identifier of the login
    lastLogin?: string, // the timestamp when this user last logged in
    division?: string, // The division that this Login belongs to.
    parentDivision?: string, // The parent division that this Login belongs to. Children of this Login will inherit its parent division.
    roles: number, // The roles associated with this Login, specified as an integer.
    confirmed: PayrixBool, // Whether the email associated with this Login was confirmed. This field is stored as an integer and will be set to '1' when the email is confirmed.
    username?: string,
    password?: string,
    first: string,
    middle?: string,
    last: string,
    email: string,
    allowedResources: any, // 128 if you are creating an application, or a return struct like {“create”: ["accounts","logins","apikeys","sessions"],"update":["accounts","apikeys","sessions"]}
    restrictedResources: string,
    portalAccess: PayrixBool,
    mfaEnabled: PayrixBool,
    mfaSecret?: string,
    mfaEnrolledDate?: PayrixDateYearMonthDayHourSecondMillis, // date as a string YYYY-MM-DD HH24:mm:ss.sss
    mfaType?: string,
    address1?: string,
    address2?: string,
    city?: string,
    state?: string,
    zip?: string,
    country?: string,
    phone?: string,
    fax?: string,
    inactive: PayrixBool,
    frozen: PayrixBool,
    ssoId?: string,
    effectiveRoles: number // see roles
}

export type PayrixOrg = {
    id: PayrixEntity; // unique id for this Merchant
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    login: string; // the identifier of the login
    name: string; //
    description: string; // The description of the Merchant (1-100 characters)
}

// A subscription, a recurring payment plan.
export const ZPayrixSubscription = ZPayrixEntityHeader.extend({
    order: ZPayrixEntity.optional(), // The order of the Txn that will be created through this Subscription.
    txnDescription: z.string().optional(), // The description of the Txn that will be created through this Subscription.
    descriptor: z.string().optional(), // The descriptor (50 chars) that will appear on the customer's statement for this Subscription.
    authentication: z.string(),
    authenticationId: z.string().optional(),
    failures: z.number(),
    finish: ZPayrixDateYearMonthDay.optional(), // The date on which the Subscription should end. The date is specified as an eight digit string in YYYYMMDD format, for example, '20160120' for January 20, 2016. The value of this field must represent a date in the future.
    firstTxn: ZPayrixEntity.optional(), // The identifier of the first Txn that was created through this Subscription.
    maxFailures: z.number().optional(),
    origin: ZSubscriptionOrigin, // The origin of the Txn that will be created through this subscription. Always 2 for us.
    plan: ZPayrixEntity, // The identifier of the Plan that this Subscription is associated with. The Plan determines the frequency and amount of each payment.
    statementEntity: ZPayrixEntity.optional(),
    start: ZPayrixDateYearMonthDay, // The date on which the Subscription should start. The date is specified as an eight digit string in YYYYMMDD format, for example, '20160120' for January 20, 2016. The value of this field must represent a date in the future.
    tax: z.number().optional(), // The tax amount for the Subscription. This field is specified as an integer in cents.
});
export type PayrixSubscription = z.infer<typeof ZPayrixSubscription>;

export enum ETokenStatus {
    Pending = 'pending',
    Ready = 'ready'
}

export const ZTokenStatus = z.enum(ETokenStatus);
export type TokenStatus = z.infer<typeof ZTokenStatus>;

export enum ETokenType {
    OminiToken = 'omnitoken',
    NetworkToken = 'networktoken',
    Internal = 'internal'
}

export const ZTokenType = z.enum(ETokenType);
export type TokenType = z.infer<typeof ZTokenType>;

// Token information
export const ZPayrixToken = ZPayrixEntityHeader.extend({
    customer: ZPayrixEntity, // the customer id
    payment: ZPaymentInfo.partial().optional(),
    token: z.string(), // autogenerated token
    expiration: MonthYearAsMMYY, // The expiry month for the payment method associated with this Token. This field is stored as a text string in 'MMYY' format. Null if bank account.
    name: z.string().nullable(), // the name of the account (for reference)
    description: z.string().nullable(), // a description of the account (for reference). We use this to store meta information.
    custom: z.string().optional().nullable(), // Custom, free-form field for client-supplied text, 0-1000 characters long. This is where we can store custom information.
    authTokenCustomer: z.string().optional().nullable(),
    status: ZTokenStatus,
    type: ZTokenType.optional().nullable(),
    internalToken: z.string().optional().nullable(),
    origin: z.string().nullable(),
    entryMode: z.string().nullable(),
    omnitoken: z.string().nullable()
});
export type PayrixToken = z.infer<typeof ZPayrixToken>;

/** This is what is returned when we expand on 'token' when requesting a TXN */
export const ZPayrixTokenExpanded = ZPayrixEntityHeader.extend({
    customer: ZPayrixEntity, // the customer id
    token: z.string(), // autogenerated token
    expiration: MonthYearAsMMYY, // The expiry month for the payment method associated with this Token. This field is stored as a text string in 'MMYY' format. Null if bank account.
    name: z.string(), // the name of the account (for reference)
    description: z.string(), // a description of the account (for reference)
    custom: z.string(), // Custom, free-form field for client-supplied text, 0-1000 characters long. This is where we can store our Id
    authTokenCustomer: z.string().optional(),
    status: ZTokenStatus,
    origin: z.string().optional(),
    entryMode: z.string().optional(),
    omnitoken: z.string().optional()
});
export type PayrixTokenExpanded = z.infer<typeof ZPayrixTokenExpanded>;

/** This is what is returned when we expand on 'payment' when requesting a TXN */
export const ZPayrixPaymentExpanded = z.object({
    id: z.string().optional(), // e.g. p1_pmt_664d22428dd8f1afbeddd50,
    method: ZPaymentMethods, // e.g. 2,
    number: z.string(), // last four of the number, e.g. 1036 (same as last4?)
    routing: z.string().optional().nullable(), // last four of the routing, or 0 if a card
    bin: z.string().optional().nullable(), // bin number, e.g. 446542
    payment: z.string().optional().nullable(), // always null?
    lastChecked: z.string().optional().nullable(), // always null?
    last4: z.string().optional().nullable(), // last four of the number, e.g. 1036
    mask: z.string().optional().nullable(), // always null?
    plaidConsumerAccount: z.string().optional().nullable(), // Plaid account
});
export type PayrixPaymentExpanded = z.infer<typeof ZPayrixPaymentExpanded>;

/** Result from a call to tokens with the payment expanded */
export const ZPayrixTokenWithPayment = ZPayrixToken.extend({
    payment: ZPayrixPaymentExpanded
});
export type PayrixTokenWithPayment = z.infer<typeof ZPayrixTokenWithPayment>;

// export type PayrixTokenWithPayment = PayrixToken & {
//     payment: PayrixPaymentExpanded;
// }

const ZPayrixTokenCustom = z.object({
    id: ZPayrixEntity, // Payment ID
    method: ZPaymentMethods,
    methodName: ZPaymentMethodNames,
    number: z.string().length(4), // last 4 of the card or bank account
    routing: z.string().length(4).optional(), // last 4 of the routing number for this token
    cardType: ZCardPaymentMethodNames.optional(), // @see CardType
    bin: z.string(), // The bin (run) number
    lastChecked: z.number().optional(), // Payrix date as a YYYYMMDD number
    mask: z.string().optional(),
    phone: z.string(), // phone z.number() associated with this token
    email: z.email('Not a valid email'), // email address associated with this token
    address: ZPayrixAddress, // billing address associated with this token
});
export type PayrixTokenCustom = z.infer<typeof ZPayrixTokenCustom>;

export const ZPayrixPayout = ZPayrixEntityHeader.extend({
    account: z.string(), // The token of the Account that this Payout is associated with. This account will either receive the funds or be debited for the funds every time a Disbursement occurs, depending on the direction of the Disbursement.
    entity: ZPayrixEntity, // The identifier of the Entity that this Payout is associated with (bank account entity for IOLTA or operating)
    payoutFlow: z.string().optional(), // not used
    name: z.string().optional(), // the name of this payout
    description: z.string().optional(), // the description of this payout (max 100 chars)
    schedule: ZPayoutSchedule, // The schedule that determines when this Payout is triggered to be paid. We always use 5 - single.
    scheduleFactor: z.number().optional(), // A multiplier that you can use to adjust the schedule set in the 'schedule' field.
    start: ZPayrixDateYearMonthDay, // Start data in YYYYMMDD format. Must be current or future date
    currency: z.string().optional(), // The currency of the amount in this payout.  This field is only required when Um is set to ACTUAL.
    um: ZPayoutUnit, // The unit of measure for this payout. For example, if the Entity has a negative balance of $10 and the amount is set to 10000 (100%), then $10 will be drawn from their account to fully replenish the balance to $0.
    amount: z.number(), // The amount of this payout in cents
    minimum: z.number().optional(), // The threshold that will ensure no disbursement is generated if it doesn't reach the minimum value.
    float: z.number().optional(), // an optional minimum balance you want to maintain, despite any payouts occurring.
    skipOffDays: ZPayrixBool.optional(), // true to skip holidays and weekends, false otherwise
    sameDay: ZPayrixBool.optional(), // true for same day payout
    maximum: z.number().optional(), // The maximum threshold for a disbursement. Any amount in a disbursement exceeding this value will not be released, and will roll over to the next disbursement.
    billing: ZPayrixEntity.optional(), // The identifier of a Billing that this Payouts is associated with. Payouts associated with a Billing record will be used to pay for Statements.
    secondaryDescriptor: z.string().optional(), // The secondary billing descriptor to appear on the bank statements for the payout.
});
export type PayrixPayout = z.infer<typeof ZPayrixPayout>;

export enum EBatchStatus {
    Open = 'open',
    Processed = 'processed',
}

export const ZBatchStatus = z.enum(EBatchStatus);
export type BatchStatus = z.infer<typeof ZBatchStatus>;

export const ZPayrixBatch = ZPayrixEntityHeader.extend({
    merchant: ZPayrixEntity, // The id of the Merchant (not the Merchant's entity)
    date: ZPayrixDateYearMonthDay, // The date on which this Batch was created. The date is specified as an eight digit string in YYYYMMDD format.
    status: ZBatchStatus, // The status of the Batch
});
export type PayrixBatch = z.infer<typeof ZPayrixBatch>;


export enum EUnauthReason {
    Incomplete = 'incomplete',
    Timeout = 'timeout',
    ClerkCancelled = 'clerkCancelled',
    CustomerCancelled = 'customerCancelled',
    MisDispense = 'misdispense',
    HardwareFailure = 'hardwareFailure',
    SuspectedFraud = 'suspectedFraud',
}

export const ZUnauthReason = z.enum(EUnauthReason);
export type UnauthReason = z.infer<typeof ZUnauthReason>;

export enum ETxnResultType {
    General = 1, //A general type of result.
    FraudPrevention = 2, // Fraud prevention alert.
    Processor = 3, // Processor error.
    CVVMismatch = 4, // CVV matching alert.
    AVSCheck = 5, // AVS check alert.
    AAVSCheck = 6, // AAVS check alert.
    NetworkError = 7, // Network error.
    ThreeDSAlert = 8, // 3DS check alert.
}

export const ZTxnResultType = z.enum(ETxnResultType);
export type TxnResultType = z.infer<typeof ZTxnResultType>;

export enum ETxnResultCode {
    Approved = 0, // Transaction approved.
    PartiallyApproved = 1, // Partially approved. The processor has only approved a portion of the total transaction amount.
    Declined = 2, // Declined. The processor has declined the Transaction.
    VerificationSuccessful = 3, // Verification successful, values provided matched.
    VerificationUnsuccessful = 4, // Verification unsuccessful, values provided did not match.
    ZipCodeMismatch = 5, // The ZIP code in the Transaction data does not match the customer details held by the card issuer.
    AddressMismatch = 6, // The address in the Transaction data does not match the customer details held by the card issuer.
    NameMismatch = 7, // The name in the Transaction data does not match the customer details held by the card issuer.
    NameAndPhoneMismatch = 8, // The name and phone number in the Transaction data do not match the details held by the card issuer.
    NameAndEmailMismatch = 9, // The name and email address in the Transaction data do not match the customer details held by the card issuer.
    PhoneMismatch = 10, // The phone number in the Transaction data does not match the customer details held by the card issuer.
    PhoneAndEmailMismatch = 11, // The phone number and email address in the Transaction data do not match the customer details held by the card issuer.
    EmailMismatch = 12, // The email address in the Transaction data does not match the customer details held by the card issuer.
    NameNotInTxnData = 13, // The customer name could not be found in the Transaction data.
    NameAndPhoneNotInTxnData = 14, // The customer name and phone number were not found in the Transaction data.
    NameAndEmailNotInTxnData = 15, // The customer name and email address were not found in the Transaction data.
    PhoneNotInTxnData = 16, // The customer phone number was not found in the Transaction data.
    PhoneAndEmailNotInTxnData = 17, // The customer phone number and email address were not found in the Transaction data.
    EmailNotInTxnData = 18, // The customer email address was not found in the Transaction data.
    CustomerNotInTxnData = 19, // Information about the customer was not found in the Transaction data.
    NonSufficientFunds = 20, // Non-sufficient funds. The customer did not have sufficient credit or balance to cover the Transaction.
    AccountInvalid = 21, // The account in the Transaction data is not valid.
    AccountUnauthorized = 22, // The account is unauthorized.
    GeneralError = 23, // General error.
    ZipNotInTxnDta = 24, // The ZIP code in the Transaction data was not verified.
    ZipAndAddressNotInTxnData = 25, // The ZIP code and the Address in the Transaction data were not verified.
    AddressNotInTxnData = 26, // The Address in the Transaction data was not verified.
    NotCaptured = 27, // Txn did not fail but was not captured.
    ThreeDSPassed = 28, // 3DS authentication passed.
    ThreeDSInvalid = 29, // 3DS authentication is invalid.
    ThreeDSFailed = 30, // 3DS authentication failed.
    ThreeDSNotValidated = 31, // 3DS authentication was not validated.
    ThreeDSAuthPassedLiabilityShifted = 32, // 3DS authentication passed but liability was not shifted.
}

export const ZTxnResultCode = z.enum(ETxnResultCode);
export type TxnResultCode = z.infer<typeof ZTxnResultCode>;

export const ZPayrixTxnResult = ZPayrixEntityHeader.extend({
    txn: ZPayrixEntity, // The related transaction
    type: ZTxnResultType, // What kind of alert
    message: z.string().nullable(), //
    code: ZTxnResultCode, // The result code for this alert
    bankCode: z.string().nullable(), // e.g. transaction_not_alled or rejected_lost/stolen_checks
    originalCode: z.string().nullable(), // e.g. "10", "M",
});
export type PayrixTxnResult = z.infer<typeof ZPayrixTxnResult>;

export const ZPayrixTransaction = ZPayrixEntityHeader.extend({
    address1: z.string().max(500).nullable(), // The first line of the address associated with this Entity
    address2: z.string().max(500).nullable(), // The second line of the address
    allowPartial: ZPayrixBool, // Required: allow partial payment
    approved: z.number().nullable(), // The total amount that was approved for this Transaction by the processor, in cents
    authCode: z.string().nullable(), // The authorization code for this Transaction
    authDate: ZPayrixDateYearMonthDay.nullable(), // The date on which the Transaction was authorized, in YYYYMMDD format
    authentication: z.string().nullable(), // Authentication token returned by the network in a 3DSecure txn.
    authenticationId: z.string().nullable(), // Optional transaction ID returned by the network in a 3DSecure txn.
    authorization: z.string().nullable(), // The authorization code for this Transaction, as returned by the network
    authTokenCustomer: ZPayrixEntity.nullable(), // The customer identifier from the AuthToken used during authentication.
    batch: ZPayrixEntity.nullable(), // If the Transaction is linked to a Batch, this field specifies the identifier of the Batch
    captured: z.string().nullable(), // A timestamp indicating when this Transaction was captured
    cashback: z.number().nullable(), // The amount of the total sum of this Transaction that is given as cash back, in cents
    channel: z.string().max(1000).nullable(), // This field is stored as a text string and must be between 0 and 1000 characters long.
    city: z.string().max(500).nullable(), // The city associated with the address
    clientIp: z.string().nullable(), // The client ip address from which the Entity was created, valid values are any Ipv4 or Ipv6 address
    cofType: ZCardOnFileType.nullable(), // The type of Card On File transaction when using a token
    company: z.string().nullable(), // company name, critical if this was a company check
    copyReason: z.enum(['resubmission', 'reauthorization']).nullable(), // Reason for copying Transaction referenced in fromtxn field
    country: z.string().length(3).nullable(), // 3-character country code
    currency: z.string().length(3), // 3-letter currency name, e.g. USD
    currencyConversion: z.enum(['customerAccepted', 'customerRejected', 'notEligible']).nullable(),
    cvv: ZPayrixBool, // Whether correct CVV was sent during this Transaction
    cvvStatus: z.enum(['notPresent', 'illegible', 'notProvided']).nullable(), // The status of the CVV on the card
    debtRepayment: ZPayrixBool, // If this transaction is for debt repayment.
    description: z.string().max(1000).nullable(), // This can be anything
    descriptor: z.string().nullable(), // Firm name
    discount: z.number(), // The discount applied to the transaction (as cents)
    duty: z.number(), // The duty free applicable to this transaction (as cents)
    email: z.string().nullable(), // email for this entity
    emv: ZPayrixBool, // 0 = not captured, 1 = captured
    entryMode: ZEntryMode.nullable(), // How payment information has been entered
    expiration: MonthYearAsMMYY, // MMYY
    fee: z.string().nullable(), // Optional calculated fee amount indicator. This should be used in conjunction with txnFee setting on Fees resource. This field is specified as an integer in cents.
    first: z.string().nullable(), // first name
    fortxn: ZPayrixEntity.nullable(), // If this Transaction is related to another Transaction, then this field is set to the identifier of the other Transaction
    fromtxn: ZPayrixEntity.nullable(), // Reauthorize this referenced Transaction. For example, to process a resubmission of a declined Transaction or to reauthorize an expired Transaction
    funded: z.number().nullable(), // If funded, date funded as YYYYMMDD
    fundingCurrency: z.string().length(3).nullable(), // The funding currency of the txn
    fundingEnabled: ZPayrixBool, // Whether funding is enabled for the Transaction
    imported: ZPayrixBool, // Whether the txn was imported from a report or not, set automatically
    ipCreated: z.string(), // The incoming ip address from which this Entity was created
    ipModified: z.string(), // The incoming ip address from which this Entity was last modified
    last: z.string().nullable(), // last name
    merchant: ZPayrixEntity, // The id of the Merchant (not the Merchant's entity)
    middle: z.string().nullable(), // middle name
    misused: ZPayrixBool.nullable(), // Indicates if an authorization has been misused by not being captured or reversed within the timeframe
    mobile: ZPayrixBool.nullable(), // Indicates if a transaction is being processed through a Mobile POS
    networkTokenIndicator: ZPayrixBool, // Indicates if a transaction qualified for a network token through Network Payment Manager
    order: z.string().max(1000).nullable(), // The identifier of the Order associated with this Transaction. This field is stored as a text string and must be between 0 and 1000 characters long.
    origin: ZTransactionOrigin, // The origin of this Transaction
    originalApproved: z.number(),
    payment: z.string().optional().nullable(), // the payment info, if not using a token
    phone: z.string().nullable(), // primary phone number for this entity
    pin: ZPayrixBool,
    pinEntryCapability: z.enum(['unknown', 'capable', 'notCapable', 'pinPadDown']).nullable(),
    platform: ZTransactionPlatform, // platform, e.g. Apple, Google, Worldpay, etc
    processedSequence: z.number().nullable(),
    refunded: z.number().nullable(), // The amount of this Transaction that has been refunded in cents
    requestSequence: z.number().nullable(),
    reserved: ZAccountReserved, // Indicates whether the Transaction is reserved and the action that will be taken as a result
    returned: ZPayrixDateYearMonthDay.nullable(), // A date indicating when this Transaction was returned
    settled: z.number().nullable(), // A date indicating when this Transaction was settled, e.g. 20240903
    settledCurrency: z.string().length(3).nullable(), // 3-letter currency
    settledTotal: z.number().nullable(), // integer in cents, set automatically
    shipping: z.number().nullable(), // The shipping fee pertaining to this transaction, in cents
    signature: ZPayrixBool, // Whether a signature was captured during this Transaction
    softPosDeviceTypeIndicator: z.string().nullable(), // The device type indicator for the terminal
    softPosId: z.string().nullable(), // The software POS ID
    state: z.string().min(2).max(100).nullable(), // Two-letter code for a state or province
    status: ZTransactionStatus, // The status of the Transaction
    statement: ZPayrixEntity.nullable(), // Statement associated with this Transaction
    subscription: ZPayrixEntity.nullable(), // Related subscription
    surcharge: z.number().nullable(), // The amount of the total sum of this Transaction that is made up of surcharge. This field is specified as an integer in cents.
    swiped: ZPayrixBool, // 0 = not swiped, 1 = swiped
    tax: z.number().nullable(), // The amount of the total sum of this Transaction that is made up of tax, in cents
    terminal: ZPayrixEntity.nullable(), // The identifier of the terminal that processed this Transaction
    terminalCapability: ZTerminalCapability.nullable(), // Capabilities of the terminal device
    tmxSessionId: z.string().optional().nullable(), // Threatmetrix session ID, used to trace the financial session and prevent possible frauds.
    token: ZPayrixEntity.nullable(), // the ID of the payment token to use, if not using payment
    total: z.number(), // The total amount of this Transaction, in cents
    txnsession: z.string().nullable(),
    type: ZTransactionType, // type of transaction as a string
    unattended: ZPayrixBool.nullable(), // Whether the card was swiped at an unattended terminal during this Transaction
    unauthReason: ZUnauthReason.nullable(),
    zip: z.string().max(20).nullable(), // ZIP/Postal code as a z.string()
});
export type PayrixTransaction = z.infer<typeof ZPayrixTransaction>;

export const ZPayrixTransactionWithPayment = ZPayrixTransaction.extend({
    payment: ZPaymentInfo
});

export type PayrixTransactionWithPayment = z.infer<typeof ZPayrixTransactionWithPayment>;


export const ZPayrixTransactionFull = ZPayrixTransaction.extend({
    payment: ZPaymentInfo.optional().nullable(),
    // OWA-714: mirrors the server schema. `/txns` returns `token` as the bare
    // 32-char token string even when asked to expand it — and the client gets
    // these straight off `/payrix/payments/range`, which passes the Payrix
    // response through with that same non-working `expand[token][customer][]`.
    // Both shapes are accepted; callers that need the customer must narrow.
    token: z.union([
        z.string(),
        ZPayrixToken.extend({
            customer: ZPayrixCustomer.optional().nullable()
        }),
    ]).optional().nullable(),
    // OWA-714: `subscription` has the same expanded-vs-bare ambiguity, and the
    // BASE ZPayrixTransaction already types it as a bare id string — only this
    // Full override narrowed it to the expanded object. parsePaymentMeta accepts
    // either shape, so the schema must too; otherwise a real bare subscription
    // parses as an error while the code handles it fine. Which shape Payrix
    // actually sends here is still unverified (OWA-727).
    subscription: z.union([
        ZPayrixEntity,
        ZPayrixSubscription,
    ]).optional().nullable(),
    txnResults: z.array(ZPayrixTxnResult).optional().nullable(),
});

export type PayrixTransactionFull = z.infer<typeof ZPayrixTransactionFull>;

/** This is the final return structure when requesting a TXN with payment and token expanded */
export const ZParsePaymentTransaction = ZPayrixTransaction.extend({
    typeText: ZTransactionTypeName,
    statusText: ZTransactionStatusName,
    method: z.object({
        type: ZPaymentType,
        number: z.string(),
        routing: z.string().optional().nullable(),
        cardType: ZCardPaymentMethodNames.optional().nullable(),
        accountType: ZBankPaymentMethodNames.optional().nullable(),
        fingerprint: z.string().optional().nullable()
    }),
    payment: ZPayrixPaymentExpanded,
    token: ZPayrixTokenExpanded,
    amount: z.number(),
    amount_refunded: z.number().optional().nullable(),
    chargeDate: z.date(),
    voidOrRefundDate: z.date().optional().nullable()
});
export type ParsePaymentTransaction = z.infer<typeof ZParsePaymentTransaction>;

// Represents a transaction description
export const ZDescription = z.object({
    id: ZUuid, // this is our transactionId from Outlaw
    isTrust: z.boolean(), // was this transaction processed against a trust (IOLTA) account?
    description: z.string(), // user or system-entered description
});
export type Description = z.infer<typeof ZDescription>;

// Represents a pending entry in Payrix
export type PayrixPendingEntry = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // id of the Login that last modified the entity
    entity: PayrixEntity; // the id of the Entity that owns this Disbursement
    onentity: PayrixEntity; // The identifier of the Entity that this PendingEntry refers to. This is the owner of the record that triggered the charge.
    fromentity: PayrixEntity; // If the activity that this PendingEntry refers to involves two parties in the system with one paying a charge of any kind, then this field stores the identifier of the Entity that the charge or other activity is for.
    fund: string; // The identifier of the Fund that this PendingEntry refers to.
    opposingPendingEntry: PayrixEntity;
    entry: string;
    adjustment: any;
    chargeback: PayrixEntity; // If the activity that this PendingEntry refers to is a Chargeback, then this field stores the identifier of the corresponding Chargeback resource.
    disbursement: PayrixEntity; // If the activity that this PendingEntry refers to is the charging of a Disbursement, then this field stores the identifier of the corresponding Disbursement resource.
    fee: PayrixFee; // If the activity that this PendingEntry refers to is the charging of a Fee, then this field stores the identifier of the corresponding Fee resource.
    txn: PayrixEntity; // If the activity that this PendingEntry refers to is the paying of a Refund, then this field stores the identifier of the corresponding Refund resource.
    event: EEventType; // The type of event that triggered this PendingEntry resource
    eventId: PayrixEntity; // The identifier of the record that is associated with this PendingEntry.
    description: string; // A description of this PendingEntry.
    amount: number; // The amount involved in this PendingEntry. It refers to the amount charged, transferred, or disbursed. This field is specified as an integer in cents.
    pending: PayrixBool; // Whether this resource is marked as pending.
    profitShare: PayrixEntity; // If the activity that this PendingEntry refers to is a ProfitShare, then this field stores the identifier of the corresponding ProfitShare resource.
    originalCurrency: string; // three letter currency code
    currencyRate: number;
    terminalTxn: PayrixBool;
    isFee: PayrixBool;
    entityDebt: string;
    statement: PayrixEntity; // If the activity that this PendingEntry refers to is a Statement, then this field stores the identifier of the corresponding Statement resource.
    settlement: PayrixEntity; // If the activity that this PendingEntry refers to is a Settlement, then this field stores the identifier of the corresponding Settlement resource.
    originalEventId: PayrixEntity;
    originalEvent: EEventType;
    revShareStatement: PayrixEntity; // If the activity that this PendingEntry refers to is a Revenue Share, then this field stores the identifier of the corresponding RevShareStatement resource
};


/**
 * These fields mainly come from a PayrixEntry result
 */
export const ZPayrixFeeSummary = z.object({
    id: ZPayrixEntity, // Fee ID
    txn: ZPayrixEntity, // Transaction ID
    event: ZEventType, // Event Type
    eventText: z.string(),
    amount: z.number(),
    name: z.string(),
    um: z.number(),
    feeId: z.string(),
    feePct: z.number().optional(),
    feeFixed: z.number().optional()
});
export type PayrixFeeSummary = z.infer<typeof ZPayrixFeeSummary>;

export const ZPayrixFee = ZPayrixEntityHeader.extend({
    forentity: ZPayrixEntity,
    org: ZPayrixEntity.nullable(),
    type: ZFeeType,
    name: z.string().nullable(),
    description: z.string().nullable(),
    schedule: ZFeeSchedule,
    scheduleFactor: z.number(),
    start: ZPayrixDateYearMonthDay,
    finish: ZPayrixDateYearMonthDay.nullable(),
    collection: ZFeeCollection,
    collectionFactor: z.number().nullable(),
    collectionOffset: z.number().nullable(),
    um: ZFeeUnit,
    amount: z.number(),
    currency: z.string(),
    txnFee: ZPayrixBool.nullable(),
    collectionIncludeCurrent: ZPayrixBool.nullable(),
    maximum: z.number().nullable()
});

export type PayrixFee = z.infer<typeof ZPayrixFee>;

// Represents an entry in Payrix
export const ZPayrixEntry = ZPayrixEntityHeader.extend({
    entity: ZPayrixEntity, // the id of the Entity that owns this Disbursement
    onentity: ZPayrixEntity, // The identifier of the Entity that this Entry refers to. This is the owner of the record that triggered the charge.
    fromentity: ZPayrixEntity.optional(), // If the activity that this Entry refers to involves two parties in the system with one paying a charge of any kind, then this field stores the identifier of the Entity that the charge or other activity is for.
    opposingEntry: ZPayrixEntity.optional(),
    fund: ZPayrixEntity, // The identifier of the Fund that this Entry refers to.
    fee: ZPayrixFee.optional(), // If the activity that this Entry refers to is the charging of a Fee, then this field stores the identifier of the corresponding Fee resource.
    disbursement: ZPayrixEntity.optional(), // If the activity that this Entry refers to is the charging of a Disbursement, then this field stores the identifier of the corresponding Disbursement resource.
    refund: ZPayrixEntity.optional(), // If the activity that this Entry refers to is the paying of a Refund, then this field stores the identifier of the corresponding Refund resource.
    txn: ZPayrixEntity.optional(), // If the activity that this Entry refers to is the paying of a Refund, then this field stores the identifier of the corresponding Refund resource.
    chargeback: ZPayrixEntity.optional(), // If the activity that this Entry refers to is a Chargeback, then this field stores the identifier of the corresponding Chargeback resource.
    adjustment: ZPayrixEntity.optional(),
    event: ZEventType, // The type of event that triggered this Entry resource
    eventId: ZPayrixEntity, // The identifier of the record that is associated with this Entry (e.g. a transaction)
    description: z.string(), // A description of this Entry.
    amount: z.number(), // The amount involved in this Entry. It refers to the amount charged, transferred, or disbursed. This field is specified as an integer in cents.
    unsourced: z.any(),
    pending: ZPayrixBool, // Whether this resource is marked as pending.
    profitShare: ZPayrixEntity.optional(), // If the activity that this Entry refers to is a ProfitShare, then this field stores the identifier of the corresponding ProfitShare resource.
    originalCurrency: ZPayrixCurrency.optional(), // three letter currency code
    currencyRate: z.number().optional(),
    isFee: ZPayrixBool,
    statement: ZPayrixEntity.optional(), // If the activity that this Entry refers to is a Statement, then this field stores the identifier of the corresponding Statement resource.
    settlement: ZPayrixEntity.optional(), // If the activity that this Entry refers to is a Settlement, then this field stores the identifier of the corresponding Settlement resource.
    archiveSummary: ZPayrixBool, // Summary record for archived entries
    pendingEntry: ZPayrixEntity, // The identifier of the PendingEntry that this Entry is associated with.
    pendingEntryCreated: ZPayrixDateYearMonthDayHourMinuteSecond, // The date and time when the PendingEntry was created.on which the PendingEntry was created.
    originalEventId: ZPayrixEntity,
    originalEvent: ZEventType,
    revShareStatement: ZPayrixEntity.optional(), // If the activity that this Entry refers to is a Revenue Share, then this field stores the identifier of the corresponding RevShareStatement resource
});
export type PayrixEntry = z.infer<typeof ZPayrixEntry>;

export type TransactionFee = {
    id: PayrixEntity,
    txn: PayrixEntity,
    event: EEventType,
    eventText: string,
    amount: number,
    name: string,
    um: number,
    feeId: PayrixEntity,
    feePct?: number, // If the fee is a fixed amount
    feeFixed?: number, // If the fee is a percentage
}

export type PayrixEntryEnhanced = PayrixEntry & {
    eventId: PayrixTransactionWithPayment,
    fee: PayrixFee
}

export type Fee = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // id of the Login that last modified the entity
    entity: PayrixEntity; // the id of the Entity that owns this Disbursement
    forentity: PayrixEntity;
    org: PayrixEntity;
    type: number; // TODO: Need to find the available values
    name: string; // the name of the fee (created by the user)
    description: string;
    schedule: ScheduleFactor; // The schedule that determines when the subscription related to this Plan is triggered
    scheduleFactor: number; // A multiplier that you can use to adjust the schedule set in the 'schedule' field, such as daily, weekly, monthly, or annually. This field is specified as an integer and its value determines how the interval is multiplied.
    start: PayrixDateYearMonthDay; // Payrix date as YYYYMMDD
    finish: null;
    collection: null;
    collectionFactor: null;
    collectionOffset: null;
    um: EPayoutUnit; // TODO: This may be wrong
    amount: number;
    currency: string; // USD
    inactive: PayrixBool;
    frozen: PayrixBool;
    txnFee: number;
    collectionIncludeCurrent: any;
    maximum: any;
}

export type PayrixChargebackAlert = {
    subject: string;
    disputeId: PayrixEntity;
    disputeCaseId: string;
    disputeCreated: string; // date in MM-dd-yyyy format
    disputeCycle: EChargebackCycle;
    disclaimer: boolean;
    disputeReasonCode: string; // e.g. 13.7
    disputeReason: string;
    disputeResponseDue: string; // date in MM-dd-yyyy format
    disputeStatus: ChargebackStatus;
    paymentsLastFour: string;
    paymentType: string; // e.g. Visa
    txnId: PayrixEntity;
    txnAmount: number; // example is in $.cc, but has to be in cents
    txnCardHolder: string; // cardholder name?
    txnCreated: string; // date in MM-dd-yyyy format
};

// Represents a chargeback in Payrix
export const ZPayrixChargeback = ZPayrixEntityHeader.extend({
    merchant: ZPayrixEntity, // the id of the Merchant that owns this Chargeback
    txn: ZPayrixEntity, // the Transaction associated with the Chargeback
    description: z.string(), // Description 1-100 chars
    total: z.number(), // The total amount of the chargeback, in cents.
    representedTotal: z.number(), // The representedTotal for this Chargeback if it has been represented, in cents.
    cycle: ZChargebackCycle, // what part of the chargeback cycle are we in?
    currency: z.string(), // USD
    ref: z.string(), // the reference for the payment (from txn)
    reason: z.string(), // Textual reason for the chargeback
    reasonCode: z.string(), // The code as a number string
    issued: z.string(), // the date the chargeback was issued, in yyyyMMdd format
    received: z.string(), // the date the chargeback was received, in yyyyMMdd format
    reply: z.string(), // the date the chargeback was replied to, in yyyyMMdd format
    bankRef: z.string(),
    chargebackRef: z.string(),
    status: ZChargebackStatus, // open, closed, won or lost
    actionable: ZPayrixBool,
    paymentMethod: ZPaymentMethods,
    shadow: ZPayrixBool,
});
export type PayrixChargeback = z.infer<typeof ZPayrixChargeback>;

export const ZChargebackReturn = ZPayrixChargeback.extend({
    actionable: z.boolean(),
    total: z.number(),
    representedTotal: z.number(),
    cycleText: z.string(),
    fundsReturned: z.boolean(),
    entries: z.array(ZPayrixEntry),
    messages: z.array(ZPayrixChargebackMessage),
    documents: z.array(ZPayrixChargebackDocument),
    statuses: z.array(ZPayrixChargebackStatus)
});
export type ChargebackReturn = z.infer<typeof ZChargebackReturn>;

export type PayrixDisbursementResult = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // id of the Login that last modified the entity
    disbursement: PayrixEntity; // the identifier of the Disbursement that this DisbursementResult resource refers to
    code: EDisbursementCode;
    platform: string; // the platform, e.g. Vantiv
    message: string;
    disbursementRef: string; // seems to always be null
    amount: number; // the amount of the disbursement
    data: string; // the trace number will go here if applicable
    originalCode: EDisbursementCode;
};

export enum EDisbursementEntriesStatus {
    Pending = 'pending',
    Processing = 'processing',
    Processed = 'processed',
}

const ZDisbursementEntriesStatus = z.enum(EDisbursementEntriesStatus);
export type DisbursementEntriesStatus = z.infer<typeof ZDisbursementEntriesStatus>;

export enum EDisbursementFundingStatus {
    Pending = 'pending',
    Processed = 'processed',
}

export const ZPayrixAlertAction = ZPayrixEntityHeader.extend({
    type: z.enum(['web', 'email']),
    options: z.enum(['JSON']),
    value: z.string(),
    alert: ZPayrixEntity,
    retries: z.number().nullable(),
    headerName: z.string(),
    headerValue: z.string(),
    maxAttemptsTempDisabled: z.number(),
});
export type PayrixAlertAction = z.infer<typeof ZPayrixAlertAction>;

export const ZPayrixAlertTrigger = ZPayrixEntityHeader.extend({
    alert: ZPayrixEntity,
    event: z.string(),
    resource: z.number(),
    name: z.string(),
    description: z.string(),
});
export type PayrixAlertTrigger = z.infer<typeof ZPayrixAlertTrigger>;

export const ZPayrixAlert = ZPayrixEntityHeader.extend({
    division: ZPayrixEntity,
    forlogin: ZPayrixEntity,
    team: ZPayrixEntity.optional().nullable(),
    name: z.string(),
    description: z.string(),
    alertActions: z.array(ZPayrixAlertAction).optional(),
    alertTriggers: z.array(ZPayrixAlertTrigger).optional(),
});
export type PayrixAlert = z.infer<typeof ZPayrixAlert>;

export const ZDisbursementFundingStatus = z.enum(EDisbursementFundingStatus);
export type DisbursementFundingStatus = z.infer<typeof ZDisbursementFundingStatus>;

export const ZPayrixDisbursement = ZPayrixEntityHeader.extend({
    entity: ZPayrixEntity, // the id of the Entity that owns this Disbursement
    account: z.string(), // the token of the accounts resource used for this Disbursement
    payout: ZPayrixEntity, // The identifier of the Payouts that represents the schedule for this Disbursement
    description: z.string(), // A description of this Disbursement
    amount: z.number(), // The total amount of this disbursement in cents
    status: ZDisbursementStatus, // The current status of this Disbursement
    processed: z.string(), // A timestamp indicating when the disbursement was processed
    currency: z.string(), // USD
    payment: z.string(), // no info on this field
    expiration: ZPayrixDateYearMonthDay.nullable(), // The expiration date in YYYY-MM-DD format
    sameDay: ZPayrixBool, // whether same-day funding is enabled or disabled for this disbursement
    returnedAmount: z.number().nullable(), // the amount that has been returned within the disbursement in cents
    statement: ZPayrixEntity.nullable(), // The identifier of the Statement being paid by this Disbursement
    settlement: z.string().nullable(), // no info on this field
    lastNegativeEntry: ZPayrixEntity, // the last negative Entry processed included in the disbursement
    lastNegativePendingEntry: ZPayrixEntity, // the last negative PendingEntry processed included in the disbursement
    lastNegativeReserveEntry: ZPayrixEntity, // the last negative ReserveEntry processed included in the disbursement
    disbursementEntriesStatus: ZDisbursementEntriesStatus, // the current status of disbursementEntries creation
    lastPositiveEntry: ZPayrixEntity, // the last positive Entry processed included in the disbursement
    lastPositivePendingEntry: ZPayrixEntity, // the last positive PendingEntry processed included in the disbursement
    lastPositiveReserveEntry: ZPayrixEntity, // the last positive ReserveEntry processed included in the disbursement
    fundingStatus: ZDisbursementFundingStatus, // the last positive Entry processed included in the disbursement
    secondaryDescriptor: z.string().nullable(),
});

export type PayrixDisbursement = z.infer<typeof ZPayrixDisbursement>;

/* Converts amounts from cents (integer) to dollars and cents (float) */
const ZPayrixPayoutWithDisbursement = ZPayrixPayout.extend({
    schedule: z.string(),
    accountType: z.enum(['T', 'O']),
    disbursement: ZPayrixDisbursement.partial().extend({
        statusText: z.string(),
    }).nullable(),
});

export type PayrixPayoutWithDisbursement = z.infer<typeof ZPayrixPayoutWithDisbursement>;

export type PayrixDisbursementResponse = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // id of the Login that last modified the entity
    entity: PayrixEntity; // the id of the Entity that owns this Disbursement
    account: string; // the token of the accounts resource used for this Disbursement
    payout: PayrixEntity; // The identifier of the Payouts that represents the schedule for this Disbursement
    description: string; // A description of this Disbursement
    amount: number; // The total amount of this disbursement in cents
    status: EDisbursementStatus; // The current status of this Disbursement
    currency: string; // USD
    payment: string; // no info on this field
    expiration: PayrixDateYearMonthDay; // The expiration date in YYYY-MM-DD format
    sameDay: PayrixBool; // whether same-day funding is enabled or disabled for this disbursement
    returnedAmount: number; // the amount that has been returned within the disbursement in cents
    settlement: string; // no info on this field
    lastNegativeEntry: PayrixEntity; // the last negative Entry processed included in the disbursement
    lastNegativePendingEntry: PayrixEntity; // the last negative PendingEntry processed included in the disbursement
    lastNegativeReserveEntry: PayrixEntity; // the last negative ReserveEntry processed included in the disbursement
    disbursementEntriesStatus: 'pending' | 'processing' | 'processed'; // the current status of disbursementEntries creation
    lastPositiveEntry: PayrixEntity; // the last positive Entry processed included in the disbursement
    lastPositivePendingEntry: PayrixEntity; // the last positive PendingEntry processed included in the disbursement
    lastPositiveReserveEntry: PayrixEntity; // the last positive ReserveEntry processed included in the disbursement
    fundingStatus: 'pending' | 'processed'; // the last positive Entry processed included in the disbursement
    secondaryDescriptor: string;
};

export type PayrixDisbursementEntry = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    disbursement: PayrixEntity; // the identifier of the Disbursement that this DisbursementEntry resource refers to
    entry: PayrixEntity; // the identifier of the Entry that this DisbursementEntry resource refers to
    pendingEntry: PayrixEntity; // the identifier of the PendingEntry that this DisbursementEntry resource refers to
    reserveEntry: PayrixEntity; // the identifier of the ReserveEntry that this DisbursementEntry resource refers to
    event: EDisbursementEvent; // the type of event that triggered this DisbursementEntry resource
    eventId: PayrixEntity; // the identifier of the record that is associated with this DisbursementEntry resource
    amount: number; // the total amount of this DisbursementEntry
    amountUsed: number; // the total amount used of this DisbursementEntry
    description: string; // a description of this DisbursementEntry
};

export type PayrixFunds = {
    id: string; // the ID of this resource
    created: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    modified: PayrixDateYearMonthDayHourSecondMillis; // date as a string YYYY-MM-DD HH24:mm:ss.sss
    creator: PayrixEntity; // id of the Login that created the entity
    modifier: string; // Payrix says this is the id of the Login that last modified the entity, but in practice it is a 15 digit string like 000000000000001
    entity: PayrixEntity; // the id of the Entity that owns this Disbursement
    currency: string; // USD
    reserved: number; // The amount held in this Fund that is marked as reserved. This field is specified as an integer in cents.
    pending: number; // Amount pending (not currently available)
    available: number; // The amount held in this Fund that is currently available for disbursement. This field is specified as an integer in cents.
    inactive: PayrixBool; // Whether this resource is marked as inactive.
    frozen: PayrixBool; // Whether this resource is marked as frozen.
};

export enum ENoteType {
    Note = 'Note', // Note. Just add a note to the hold.
    Release = 'Release', // Release. Release the hold for this Hold.
    Review = 'Review', // Review. Mark the hold as having been reviewed.
    ReReview = 'ReReview', // Re-Review. If the hold was marked as reviewed, this will allow resetting the review.
    AmexSales = 'AmexSales', // Amex Sales. If the entity does sales with amex.
    BusinessSales = 'BusinessSales', // Business Sales. If the entity does business direct sales.
    ConsumerSales = 'ConsumerSales', // Consumer Sales. If the entity does consumer direct sales.
    DeliverySchedule = 'DeliverySchedule', // Delivery Schedule. The delivery schedule of the entity.
    ImmediateDeliveryPercent = 'ImmediateDeliveryPercent', // Immediate Delivery Percent. The amount of immediate deliveries with 0 day after the transaction.
    SevenDayDeliveryPercent = 'SevenDayDeliveryPercent', // Seven-Day Delivery Percent. The amount of deliveries between 1-7 days after the transaction.
    FourteenDayDeliveryPercent = 'FourteenDayDeliveryPercent', // Fourteen-Day Delivery Percent. The amount of deliveries between 8-14 days after the transaction.
    ThirtyDayDeliveryPercent = 'ThirtyDayDeliveryPercent', // Thirty-Day Delivery Percent. The amount of deliveries between 15-30 days after the transaction.
    CardPresentSales = 'CardPresentSales', // Card Present Sales. If the entity does sales with card present.
    MotoSales = 'MotoSales', // Moto Sales. If the entity does sales over the phone or mail.
    EcommerceSales = 'EcommerceSales', // Ecommerce Sales. If the entity does ecommerce sales.
    SiteVisit = 'SiteVisit', // Site Visit. If the entity had the site visit.
    GoodsSold = 'GoodsSold', // Goods Sold. The type of goods the entity sales.
    AuthorizationFlatFee = 'AuthorizationFlatFee', // Authorization Flat Fee. The flat fee agreement for each authorization.
    CapturePercentFee = 'CapturePercentFee', // Capture Percent Fee. The percentage fee agreement for each capture.
    CaptureFlatFee = 'CaptureFlatFee', // Capture Flat Fee. The flat fee agreement for each capture.
    RiskApproved = 'RiskApproved', // Risk Approved. The entity was approved.
    RiskPending = 'RiskPending', // Risk Pending. The entity is in pending status.
    RiskCancelled = 'RiskCancelled', // Risk Cancelled. The entity has a cancelled status.
    RiskDenied = 'RiskDenied', // Risk Denied. The entity has a denied status.
    RiskClosed = 'RiskClosed', // Risk Denied. The entity has a closed status.
    RiskInvestigation = 'RiskInvestigation', // Risk Investigagion. The entity is in investigation status.
    RiskPendingData = 'RiskPendingData', // Risk Pending Data. Pending Docs/Info for The entity.
    RiskFundsReleased = 'RiskFundsReleased', // Risk Funds Released. Funds released for The entity.
    RiskActivityApproved = 'RiskActivityApproved', // Risk Activity Approved. Activity approved for the entity
}

export const ZNoteType = z.enum(ENoteType);
export type NoteType = z.infer<typeof ZNoteType>;

export const ZPayrixNote = ZPayrixEntityHeader.extend({
    txn: ZPayrixEntity.optional(), // The identifier of the Txn that relates to this notes resource.
    terminalTxn: ZPayrixEntity.optional(), // The identifier of the TerminalTxn that relates to this notes resource.
    hold: ZPayrixEntity.optional(), // The identifier of the Hold that relates to this notes resource.
    entity: ZPayrixEntity.optional(), // The identifier of the Entity that relates to this notes resource.
    type: ZNoteType, // The desired type to take on the referenced Note.
    data: z.string(), // Free-form text for adding a message along with the type.
    note: z.string().optional(), // A Message/Note regarding this notes resource. This field is stored as a text string.
});
export type PayrixNote = z.infer<typeof ZPayrixNote>;

export const ZPayrixNoteDocument = ZPayrixEntityHeader.extend({
    note: ZPayrixEntity,
    type: z.string().optional(),
    name: z.string().optional(),
    custom: z.string().optional(),
    description: z.string().optional(),
    status: ZPayrixNoteDocumentStatus,
    documentType: ZPayrixNoteDocumentType.optional(),
});
export type PayrixNoteDocument = z.infer<typeof ZPayrixNoteDocument>;

export type PayoutDetails = {
    entries: PayrixDisbursementEntry[],
    results: PayrixDisbursementResult[],
    txns: PayrixTransactionFull[],
    transactions: uuid[] // Outlaw Transaction IDs
}

// OWA-920: every field but applicationStatus is optional, because a firm that
// already has an application gets back only that application's status.
export type ApplicationSubmissionResults = {
    submittedApplication?: PayrixApplication,
    /**
     * OWA-680: the outcome Payrix reported for the created merchant. A 200 from
     * Payrix only confirms the records were created — the merchant may be
     * pending, held for manual underwriting, incomplete or closed — so this is
     * what the client must speak from rather than assuming success.
     *
     * Optional because nothing validates the server response and an older
     * server omits it; submitOutcomeAlert degrades to a neutral acknowledgement.
     */
    applicationStatus?: PayrixApplicationStatusInfo,
    login?: PayrixLogin,
    uploadedFiles?: [], // Not currently used
    message?: string
}

// const PayrixPaymentMetaExample = {
//     "id": "p1_txn_66e461fb9c85072af2f01fd",
//     "merchant": "p1_mer_653d5ca507c2b17e4a0251a",
//     "type": "CHARGE",
//     "description": "{\"id\":\"ec8a0a5d-c1a3-4015-954b-087eb6bb35d0\",\"isTrust\":false,\"description\":\"Payment for earned attorney fees and expenses. Thank you!\"}",
//     "status": "AUTHORIZED",
//     "authorization_code": null,
//     "amount": 100,
//     "amount_refunded": 0,
//     "chargeDate": "2024-09-13T17:02:03.641Z",
//     "voidOrRefundDate": null,
//     "method": {
//          "type": "bank",
//          "number": "1201",
//          "routing": "0614",
//          "cardType": "Individual Checking",
//          "accountType": "Individual Checking",
//          "fingerprint": "d5be3c92bee4f03998c8a2e40c7875ef"
//     },
//     "ipCreated": "159.65.166.146",
//     "subscription": null,
//     "captured": null,
//     "settled": null,
//     "funded": null,
//     "total": 10000,
//     "settledTotal": null,
//     "refunded": 0,
//     "approved": 10000
// }

export const ZPayrixPaymentMeta = ZPayrixEntityHeader.extend({
    id: ZPayrixEntity.optional(),
    merchant: ZPayrixEntity,
    type: ZTransactionType, // Here we store the NAME of the payment type, not its number
    typeText: ZTransactionTypeName,
    description: z.string().startsWith('{').endsWith('}').includes('id').includes('isTrust').includes('description'),
    status: ZTransactionStatus,
    statusText: ZTransactionStatusName,
    authorization: z.string().optional().nullable(), // 6 digits. Note that voiding the txn removes the authorization!
    amount: z.number(), // Float: dollars and cents
    amount_refunded: z.number().optional(), // Float: dollars and cents
    chargeDate: z.date(), // e.g. 2024-04-05T15:14:23.944Z
    voidOrRefundDate: z.date().optional().nullable(), // e.g. 2024-04-05T15:14:23.944Z
    ipCreated: ZIpAddress, // IP address as string
    subscription: ZPayrixEntity.optional().nullable(), // p1_sub_[23 hex chars]
    captured: ZPayrixDateYearMonthDayHourMinuteSecond.optional().nullable(), // Date and time, non-localized, e.g. 2023-10-19 19:29:06
    settled: z.number().optional().nullable(), // PayrixDate, e.g. 20240325 YYYYMMDD
    funded: z.number().optional().nullable(), // PayrixDate, e.g. 20240324 YYYYMMDD
    total: z.number(), // Integer, amount in cents
    settledTotal: z.number().optional().nullable(), // Integer, amount in cents
    refunded: z.number().optional().nullable(), // Integer, amount in cents
    approved: z.number().optional().nullable(), // Integer, amount in cents
    method: z.object({
        type: ZPaymentType, // 'card' or 'bank'
        number: z.string(), // last4 or **** last4
        routing: z.string().optional().nullable(), // last4
        cardType: ZCardPaymentMethodNames.optional().nullable(), // Check for Individual Checking, it's in the wrong place
        accountType: ZBankPaymentMethodNames.optional().nullable(),
        fingerprint: z.string().optional().nullable(), // 32 characters, or p1_pmt_[23 hex chars], // Are the p1_pmt values an error?
    }),
    payment: ZPayrixPaymentExpanded.optional().nullable(),
});

export type PayrixPaymentMeta = z.infer<typeof ZPayrixPaymentMeta>;

/* This is a synthetic type (not from Payrix) that we have created */
export const ZPayrixPaymentMethod = z.object({
    id: ZPayrixEntity.optional(), // from token.payment.id
    paymentToken: z.string().optional(),
    method: ZPaymentMethods.optional(),
    created: ZPayrixDateYearMonthDayHourMinuteSecondMillis.optional(),
    modified: ZPayrixDateYearMonthDayHourMinuteSecondMillis.optional(),
    description: z.string().optional().nullable(),
    accountType: z.union([ZBankAccountType, ZCardPaymentMethodNames]),
    accountHolderType: ZBankAccountHolderType.optional().nullable(),
    cardType: ZCardPaymentMethodNames.optional().nullable(),
    type: ZPaymentType.optional(),
    number: z.string(), // card or bank account number (Should be all digits)
    routing: z.string().optional().nullable(),
    paymentType: ZPaymentMethods.optional(),
    expiration: MonthYearAsMMYY, // The expiry month for the payment method associated with this Token. This field is stored as a text string in 'MMYY' format. Null if bank account.
    expMonth: z.string().optional().nullable(), // 2 digits, but nullable if a bank account
    expYear: z.string().optional().nullable(), // 4 digits, but nullable if a bank account
    cvv: z.string().optional().nullable(), // 3 to four digit PIN, only used during creation
    nameOnCard: z.string().optional(),
    nickname: z.string().optional().nullable(), // e.g. Frost Bank
    phone: z.string().optional().nullable(),
    email: z.string(),
    custom: ZPayrixPaymentExpanded.optional(),
    address: ZPayrixAddress, // Outlaw Address
    contact: z.any().optional().nullable()
});

export type PayrixPaymentMethod = z.infer<typeof ZPayrixPaymentMethod>;

export type PayrixTxnCustom = {
    id: string,
    isTrust: boolean,
    description?: string,
}
